import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  LayoutDashboard, Users, Building, FileText, LogOut, User, Search, DollarSign, 
  Clock, Mail, Crown, Shield, Receipt, FileSignature, Calculator, FolderOpen,
  Menu, X, ChevronRight, TrendingUp, Eye, Send, Loader2, ClipboardCheck, 
  HelpCircle, CheckCircle2, AlertCircle, TrendingDown, Save, MessageSquare, Download, Bell,
  Upload, CheckSquare, ListTodo, FolderArchive, ExternalLink, Check, Sparkles,
  Link2, Copy, RefreshCw, Plus, CheckCircle, Zap
} from "lucide-react";

import { API_URL } from "@/utils/api";

// Updated plans matching user requirements with full feature details
const PLANS = [
  { 
    id: "free", 
    name: "Free / Starter", 
    color: "gray", 
    description: "Basic features",
    features: [
      "Up to $25K annual transactions",
      "Basic bookkeeping",
      "Invoice generator",
      "Expense tracking"
    ]
  },
  { 
    id: "growth", 
    name: "Growth", 
    color: "green", 
    description: "For growing businesses",
    features: [
      "Everything in Starter",
      "Up to $100K annual transactions",
      "Full bookkeeping + reconciliation",
      "KPI dashboards",
      "Dedicated bookkeeper"
    ]
  },
  { 
    id: "scale", 
    name: "Scale", 
    color: "purple", 
    description: "Advanced features",
    features: [
      "Everything in Growth",
      "Unlimited transactions",
      "Advanced reporting",
      "Tax strategy planning",
      "Priority support"
    ]
  },
  { 
    id: "enterprise", 
    name: "Enterprise", 
    color: "amber", 
    description: "Full suite",
    features: [
      "Everything in Scale",
      "Dedicated team of bookkeepers",
      "Custom integrations",
      "White-glove onboarding",
      "SLA guarantees"
    ]
  },
];

// Updated roles
const ROLES = [
  { id: "user", name: "User", color: "gray", description: "Standard user" },
  { id: "finance", name: "Bookkeeper", color: "blue", description: "Access to assigned accounts only" },
  { id: "finance_admin", name: "Finance Admin", color: "purple", description: "Full access except user management" },
  { id: "admin", name: "Admin", color: "red", description: "Full access + user management" },
];

const NAV_ITEMS = [
  { id: "overview", icon: LayoutDashboard, label: "Overview" },
  { id: "reviews", icon: ClipboardCheck, label: "Review Tasks", badge: true },
  { id: "users", icon: Users, label: "All Users" },
  { id: "bookkeepers", icon: Shield, label: "Bookkeepers", adminOnly: true },
  { id: "payment-links", icon: DollarSign, label: "Payment Links" },
  { id: "payouts", icon: TrendingUp, label: "Payouts", badge: true },
  { id: "invoices", icon: Receipt, label: "All Invoices" },
  { id: "documents", icon: FolderOpen, label: "All Documents", badge: true },
  { id: "contracts", icon: FileSignature, label: "All Contracts" },
  { id: "taxforms", icon: Calculator, label: "Tax Forms" },
  { id: "clients", icon: Building, label: "Clients" },
  { id: "quotes", icon: Mail, label: "Quotes & Contacts" },
];

// Predefined categories for tagging
const INCOME_CATEGORIES = [
  "Sales Revenue", "Service Income", "Interest Income", "Investment Income",
  "Rental Income", "Commission", "Refunds", "Other Income"
];

const EXPENSE_CATEGORIES = [
  "Payroll", "Rent", "Utilities", "Marketing", "Software/Subscriptions",
  "Professional Services", "Office Supplies", "Travel", "Insurance",
  "Equipment", "Inventory", "Bank Fees", "Taxes", "Other Expense"
];

export default function AdminDashboard({ user, viewOnly = false }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [taxForms, setTaxForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [newPlan, setNewPlan] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Review Tasks state
  const [reviewTasks, setReviewTasks] = useState([]);
  const [selectedReviewTask, setSelectedReviewTask] = useState(null);
  const [reviewTaskDetails, setReviewTaskDetails] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editedReviewTransactions, setEditedReviewTransactions] = useState([]);
  const [savingReviewTags, setSavingReviewTags] = useState(false);
  const [activeReviewTab, setActiveReviewTab] = useState("summary");
  
  // Payout Management state
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [masterData, setMasterData] = useState(null);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [processingPayout, setProcessingPayout] = useState(null);
  const [payoutTab, setPayoutTab] = useState("pending");
  const [transferringPayout, setTransferringPayout] = useState(null);
  
  // Auto-Payout state
  const [autoPayoutUsers, setAutoPayoutUsers] = useState([]);
  const [loadingAutoPayoutUsers, setLoadingAutoPayoutUsers] = useState(false);
  const [processingAllPayouts, setProcessingAllPayouts] = useState(false);
  
  // User Management state
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [editingUser, setEditingUser] = useState(false);
  
  // User Limits Management state
  const [limitsModalOpen, setLimitsModalOpen] = useState(false);
  const [selectedUserForLimits, setSelectedUserForLimits] = useState(null);
  const [userLimitsData, setUserLimitsData] = useState(null);
  const [limitsForm, setLimitsForm] = useState({
    invoices: 0,
    payment_links: 0,
    customers: 0,
    vendors: 0,
    custom_platform_fee: 0
  });
  const [savingLimits, setSavingLimits] = useState(false);
  
  // Plan features reference
  const PLAN_FEATURES = {
    free: { name: "Free / Starter", fee: 2.5 },
    growth: { name: "Growth", fee: 2.0 },
    scale: { name: "Scale", fee: 1.0 },
    power: { name: "Power User", fee: 1.0 },
    enterprise: { name: "Enterprise", fee: 1.0 }
  };
  
  // User Documents state
  const [documentsByUser, setDocumentsByUser] = useState([]);
  const [selectedDocUser, setSelectedDocUser] = useState(null);
  const [userDocuments, setUserDocuments] = useState([]);
  const [showUserDocsModal, setShowUserDocsModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocDetailModal, setShowDocDetailModal] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [notifyOnComment, setNotifyOnComment] = useState(true);
  const [showQuickLook, setShowQuickLook] = useState(false);
  const [quickLookDoc, setQuickLookDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Document Request state
  const [showDocRequestModal, setShowDocRequestModal] = useState(false);
  const [docRequestForm, setDocRequestForm] = useState({
    user_id: "",
    user_email: "",
    task_type: "document_upload",
    category: "bank_statements",
    title: "",
    description: "",
    due_date: "",
    priority: "medium"
  });
  const [sendingDocRequest, setSendingDocRequest] = useState(false);
  
  // Admin tasks list
  const [adminTasks, setAdminTasks] = useState([]);
  
  // DocuHub Admin state
  const [docuhubUsers, setDocuhubUsers] = useState([]);
  const [selectedDocuhubUser, setSelectedDocuhubUser] = useState(null);
  const [docuhubUserData, setDocuhubUserData] = useState(null);
  const [showDocuhubModal, setShowDocuhubModal] = useState(false);
  const [docuhubLoading, setDocuhubLoading] = useState(false);
  const [docuhubComment, setDocuhubComment] = useState("");
  const [sendingDocuhubComment, setSendingDocuhubComment] = useState(false);
  const [selectedDocuhubDoc, setSelectedDocuhubDoc] = useState(null);
  const [showDocuhubPreview, setShowDocuhubPreview] = useState(false);
  const [docuhubComments, setDocuhubComments] = useState([]);
  const [markingReviewed, setMarkingReviewed] = useState(false);
  
  // Payment Links state
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentLinkForm, setPaymentLinkForm] = useState({
    client_name: "",
    client_email: "",
    description: "",
    amount: "",
    notes: "",
    send_email: false
  });
  const [creatingPaymentLink, setCreatingPaymentLink] = useState(false);
  const [paymentLinkFilter, setPaymentLinkFilter] = useState("all");
  const [recipients, setRecipients] = useState([]);
  
  // Bookkeeper Assignment state (Admin only)
  const [bookkeepers, setBookkeepers] = useState([]);
  const [selectedBookkeeper, setSelectedBookkeeper] = useState(null);
  const [bookkeepersLoading, setBookkeepersLoading] = useState(false);
  const [bookkeeperAssignments, setBookkeeperAssignments] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningUser, setAssigningUser] = useState(false);
  const [selectedUserToAssign, setSelectedUserToAssign] = useState("");
  
  // Determine if this is a finance user (view-only mode) - finance_admin should NOT be view-only
  const isViewOnly = viewOnly || user?.role === "finance";

  useEffect(() => { fetchData(); }, []);
  
  // Fetch auto-payout users when switching to that tab
  useEffect(() => {
    if (payoutTab === "auto-payout") {
      fetchAutoPayoutUsers();
    }
  }, [payoutTab]);

  // Fetch bookkeepers when switching to bookkeepers tab (admin only)
  useEffect(() => {
    if (activeTab === "bookkeepers" && user?.role === "admin") {
      fetchBookkeepers();
    }
  }, [activeTab]);

  const fetchData = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    try {
      const [statsRes, usersRes, clientsRes, quotesRes, contactsRes, invoicesRes, documentsRes, contractsRes, taxFormsRes, reviewsRes, docsByUserRes, docuhubUsersRes, paymentLinksRes, recipientsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/users`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/clients`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/quotes`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/contacts`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/invoices`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/documents`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/contracts`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/tax-forms`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/finance/review-tasks`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/documents/by-user`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/docuhub/users`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/payment-links`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/recipients`, { credentials: "include", headers })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (quotesRes.ok) setQuotes(await quotesRes.json());
      if (contactsRes.ok) setContacts(await contactsRes.json());
      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
      if (documentsRes.ok) setDocuments(await documentsRes.json());
      if (contractsRes.ok) setContracts(await contractsRes.json());
      if (taxFormsRes.ok) setTaxForms(await taxFormsRes.json());
      if (reviewsRes.ok) setReviewTasks(await reviewsRes.json());
      if (docsByUserRes.ok) setDocumentsByUser(await docsByUserRes.json());
      if (docuhubUsersRes.ok) setDocuhubUsers(await docuhubUsersRes.json());
      if (paymentLinksRes.ok) setPaymentLinks(await paymentLinksRes.json());
      if (recipientsRes.ok) setRecipients(await recipientsRes.json());
      
      // Fetch payout data
      fetchPayoutData(headers);
    } catch (err) { console.error("Failed to load data", err); } finally { setLoading(false); }
  };

  // Payout Management functions
  const fetchPayoutData = async (existingHeaders = null) => {
    const token = localStorage.getItem("auth_token");
    const headers = existingHeaders || { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    setLoadingPayouts(true);
    try {
      const [pendingRes, historyRes, masterRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/payouts/pending`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/payouts/history`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/master-data`, { credentials: "include", headers })
      ]);
      if (pendingRes.ok) setPendingPayouts(await pendingRes.json());
      if (historyRes.ok) setPayoutHistory(await historyRes.json());
      if (masterRes.ok) setMasterData(await masterRes.json());
    } catch (err) {
      console.error("Failed to load payout data:", err);
    } finally {
      setLoadingPayouts(false);
    }
  };

  const handleProcessPayout = async (userPayout) => {
    setProcessingPayout(userPayout.user_id);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/payouts/process`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          user_id: userPayout.user_id,
          payment_link_ids: userPayout.payment_links,
          notes: `Processed batch payout for ${userPayout.link_count} payment links`
        })
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Payout processed: $${result.payout_amount.toFixed(2)} for ${result.link_count} links`);
        fetchPayoutData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to process payout");
      }
    } catch (err) {
      toast.error("Failed to process payout");
    } finally {
      setProcessingPayout(null);
    }
  };

  const handleMarkTransferred = async (payoutId) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/payouts/${payoutId}/mark-transferred`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      if (res.ok) {
        toast.success("Payout marked as transferred");
        fetchPayoutData();
      } else {
        toast.error("Failed to mark payout");
      }
    } catch (err) {
      toast.error("Failed to mark payout");
    }
  };

  const handleStripeTransfer = async (payoutId) => {
    setTransferringPayout(payoutId);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/payouts/${payoutId}/transfer`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Transfer successful! ${data.message}`);
        fetchPayoutData();
      } else {
        toast.error(data.detail || "Transfer failed");
      }
    } catch (err) {
      toast.error("Failed to execute transfer");
    } finally {
      setTransferringPayout(null);
    }
  };

  const fetchAutoPayoutUsers = async () => {
    setLoadingAutoPayoutUsers(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/auto-payout-users`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setAutoPayoutUsers(data);
      }
    } catch (err) {
      console.error("Failed to fetch auto-payout users:", err);
    } finally {
      setLoadingAutoPayoutUsers(false);
    }
  };

  const handleProcessAllAutoPayouts = async () => {
    if (!confirm("This will process all pending payouts for auto-payout enabled users. Continue?")) {
      return;
    }
    
    setProcessingAllPayouts(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/payouts/process-all-auto`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Processed ${data.processed_count} payouts totaling $${data.total_amount.toLocaleString()}`);
        fetchAutoPayoutUsers();
        fetchPayoutData();
      } else {
        toast.error(data.detail || "Failed to process payouts");
      }
    } catch (err) {
      toast.error("Failed to process auto-payouts");
    } finally {
      setProcessingAllPayouts(false);
    }
  };

  // Bookkeeper Management functions (Admin only)
  const fetchBookkeepers = async () => {
    if (user?.role !== "admin") return;
    setBookkeepersLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/bookkeepers`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        setBookkeepers(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch bookkeepers:", err);
    } finally {
      setBookkeepersLoading(false);
    }
  };

  const fetchBookkeeperAssignments = async (bookkeeperId) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/bookkeeper/${bookkeeperId}/assignments`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        setBookkeeperAssignments(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedBookkeeper || !selectedUserToAssign) return;
    setAssigningUser(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/bookkeeper/assign`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          bookkeeper_id: selectedBookkeeper.user_id,
          user_id: selectedUserToAssign
        })
      });
      if (res.ok) {
        toast.success("User assigned to bookkeeper");
        fetchBookkeeperAssignments(selectedBookkeeper.user_id);
        fetchBookkeepers();
        setShowAssignModal(false);
        setSelectedUserToAssign("");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to assign user");
      }
    } catch (err) {
      toast.error("Failed to assign user");
    } finally {
      setAssigningUser(false);
    }
  };

  const handleUnassignUser = async (userId) => {
    if (!selectedBookkeeper) return;
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/bookkeeper/unassign`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          bookkeeper_id: selectedBookkeeper.user_id,
          user_id: userId
        })
      });
      if (res.ok) {
        toast.success("User unassigned");
        fetchBookkeeperAssignments(selectedBookkeeper.user_id);
        fetchBookkeepers();
      } else {
        toast.error("Failed to unassign user");
      }
    } catch (err) {
      toast.error("Failed to unassign user");
    }
  };

  // Review Task functions
  const fetchReviewTaskDetails = async (taskId) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/finance/review-tasks/${taskId}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setReviewTaskDetails(data);
        setEditedReviewTransactions(data.analysis?.transactions || []);
        setShowReviewModal(true);
      }
    } catch (err) {
      toast.error("Failed to load review details");
    }
  };

  const handleUpdateReviewCategory = (index, newCategory) => {
    setEditedReviewTransactions(prev => prev.map((txn, i) => 
      i === index ? { ...txn, category: newCategory, is_suspense: newCategory === "Suspense" } : txn
    ));
  };

  const handleSaveReviewTags = async () => {
    if (!selectedReviewTask) return;
    setSavingReviewTags(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/finance/review-tasks/${selectedReviewTask.task_id}/transactions`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transactions: editedReviewTransactions })
      });
      if (res.ok) {
        toast.success("Review updated successfully");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to save review");
    } finally {
      setSavingReviewTags(false);
    }
  };

  const handleCompleteReview = async (taskId) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/finance/review-tasks/${taskId}/complete`, {
        method: "PUT",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        toast.success("Review marked as completed");
        setShowReviewModal(false);
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to complete review");
    }
  };

  const getReviewLiveStats = () => {
    let totalIncome = 0;
    let totalExpenses = 0;
    const suspenseItems = [];
    
    editedReviewTransactions.forEach(txn => {
      const amount = txn.amount || 0;
      if (txn.is_suspense || txn.category === "Suspense") {
        suspenseItems.push(txn);
      } else if (amount >= 0) {
        totalIncome += amount;
      } else {
        totalExpenses += Math.abs(amount);
      }
    });
    
    return { totalIncome, totalExpenses, suspenseItems };
  };

  const pendingReviewCount = reviewTasks.filter(t => t.status === "pending").length;

  // User Documents functions
  const fetchUserDocuments = async (userId) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/documents/user/${userId}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setUserDocuments(data.documents || []);
        setSelectedDocUser(data.user);
        setShowUserDocsModal(true);
      }
    } catch (err) {
      toast.error("Failed to load user documents");
    }
  };

  const handleAddComment = async () => {
    if (!selectedDocument || !newComment.trim()) return;
    setSendingComment(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/documents/comment`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          document_id: selectedDocument.document_id,
          comment: newComment,
          notify_user: notifyOnComment
        })
      });
      if (res.ok) {
        toast.success(notifyOnComment ? "Comment added and user notified" : "Comment added");
        setNewComment("");
        // Refresh documents
        if (selectedDocUser) {
          const userData = documentsByUser.find(u => u.email === selectedDocUser.email);
          if (userData) fetchUserDocuments(userData.user_id);
        }
      } else {
        toast.error("Failed to add comment");
      }
    } catch (err) {
      toast.error("Error adding comment");
    } finally {
      setSendingComment(false);
    }
  };

  const usersWithDocs = documentsByUser.filter(u => u.document_count > 0);

  // Quick Look handler
  const handleQuickLook = (doc) => {
    setQuickLookDoc(doc);
    setShowQuickLook(true);
    setPreviewLoading(true);
    // Auto-hide loading spinner after 3 seconds as fallback
    setTimeout(() => setPreviewLoading(false), 3000);
  };

  // Get preview type based on file extension
  const getPreviewType = (filename, fileType) => {
    const ext = filename?.toLowerCase().split('.').pop();
    const type = fileType?.toLowerCase();
    
    if (['pdf'].includes(ext) || type?.includes('pdf')) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext) || type?.includes('image')) return 'image';
    if (['doc', 'docx'].includes(ext)) return 'doc';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'spreadsheet';
    return 'unsupported';
  };

  // Get full file URL with auth token
  const getFileUrl = (doc) => {
    if (!doc?.file_url) return null;
    const token = localStorage.getItem("auth_token");
    // If file_url is relative, prepend API_URL
    const baseUrl = doc.file_url.startsWith('/') ? `${API_URL}${doc.file_url}` : doc.file_url;
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}token=${token}`;
  };

  const handleLogout = async () => {
    await fetch(`${API_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
    localStorage.removeItem("auth_token");
    navigate("/");
  };

  const handleRoleUpdate = async () => {
    if (!selectedUser || !newRole) return;
    const token = localStorage.getItem("auth_token");
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.user_id}/role`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include", body: JSON.stringify({ role: newRole })
      });
      if (!response.ok) throw new Error("Failed to update role");
      toast.success("Role updated successfully"); setRoleDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handlePlanUpdate = async () => {
    if (!selectedUser || !newPlan) return;
    const token = localStorage.getItem("auth_token");
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.user_id}/plan`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include", body: JSON.stringify({ plan: newPlan })
      });
      if (!response.ok) throw new Error("Failed to update plan");
      toast.success("Plan updated successfully"); setPlanDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const openRoleDialog = (u) => { setSelectedUser(u); setNewRole(u.role); setRoleDialogOpen(true); };
  const openPlanDialog = (u) => { setSelectedUser(u); setNewPlan(u.plan || "free"); setPlanDialogOpen(true); };
  const openEmailDialog = (u) => { setSelectedUser(u); setEmailForm({ subject: "", message: "" }); setEmailDialogOpen(true); };
  
  // User Edit Modal
  const [userEditModalOpen, setUserEditModalOpen] = useState(false);
  const [userEditForm, setUserEditForm] = useState({
    name: "",
    email: "",
    role: "",
    plan: "",
    company_name: "",
    phone: "",
    new_password: "",
    auto_payout_enabled: false
  });
  const [savingUser, setSavingUser] = useState(false);
  
  const openUserEditModal = (u) => {
    setSelectedUser(u);
    setUserEditForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "user",
      plan: u.plan || "free",
      company_name: u.company_name || "",
      phone: u.phone || "",
      new_password: "",
      auto_payout_enabled: u.auto_payout_enabled || false
    });
    setUserEditModalOpen(true);
  };
  
  const handleSaveUserEdit = async () => {
    if (!selectedUser) return;
    
    setSavingUser(true);
    const token = localStorage.getItem("auth_token");
    
    // Build update object - only include changed fields
    const updates = {};
    if (userEditForm.name !== selectedUser.name) updates.name = userEditForm.name;
    if (userEditForm.email !== selectedUser.email) updates.email = userEditForm.email;
    if (userEditForm.role !== selectedUser.role) updates.role = userEditForm.role;
    if (userEditForm.plan !== (selectedUser.plan || "free")) updates.plan = userEditForm.plan;
    if (userEditForm.company_name !== selectedUser.company_name) updates.company_name = userEditForm.company_name;
    if (userEditForm.phone !== selectedUser.phone) updates.phone = userEditForm.phone;
    if (userEditForm.new_password) updates.new_password = userEditForm.new_password;
    if (userEditForm.auto_payout_enabled !== selectedUser.auto_payout_enabled) updates.auto_payout_enabled = userEditForm.auto_payout_enabled;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.user_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(updates)
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`User ${selectedUser.name} updated successfully`);
        setUserEditModalOpen(false);
        fetchData();
      } else {
        toast.error(data.detail || "Failed to update user");
      }
    } catch (err) {
      toast.error("Failed to update user");
    } finally {
      setSavingUser(false);
    }
  };

  // User Limits Management Functions
  const openLimitsModal = async (u) => {
    setSelectedUserForLimits(u);
    setLimitsModalOpen(true);
    
    // Fetch user's current limits
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${u.user_id}/limits`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setUserLimitsData(data);
        setLimitsForm({
          invoices: data.custom_limits?.invoices ?? data.effective_limits?.invoices ?? 0,
          payment_links: data.custom_limits?.payment_links ?? data.effective_limits?.payment_links ?? 0,
          customers: data.custom_limits?.customers ?? data.effective_limits?.customers ?? 0,
          vendors: data.custom_limits?.vendors ?? data.effective_limits?.vendors ?? 0,
          custom_platform_fee: data.custom_platform_fee ?? data.effective_platform_fee ?? 1
        });
      }
    } catch (err) {
      toast.error("Failed to fetch user limits");
    }
  };

  const handleSaveLimits = async () => {
    if (!selectedUserForLimits) return;
    
    setSavingLimits(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUserForLimits.user_id}/limits`, {
        method: "PUT",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(limitsForm)
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success("User limits updated successfully");
        setLimitsModalOpen(false);
        fetchData();
      } else {
        toast.error(data.detail || "Failed to update limits");
      }
    } catch (err) {
      toast.error("Failed to update limits");
    } finally {
      setSavingLimits(false);
    }
  };

  const handleResetLimitsToDefaults = async () => {
    if (!selectedUserForLimits) return;
    if (!confirm("Reset all limits to plan defaults? This will remove all custom overrides.")) return;
    
    setSavingLimits(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUserForLimits.user_id}/limits`, {
        method: "PUT",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ reset_to_plan_defaults: true })
      });
      
      if (res.ok) {
        toast.success("Limits reset to plan defaults");
        setLimitsModalOpen(false);
        fetchData();
      } else {
        toast.error("Failed to reset limits");
      }
    } catch (err) {
      toast.error("Failed to reset limits");
    } finally {
      setSavingLimits(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedUser || !emailForm.subject || !emailForm.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSendingEmail(true);
    const token = localStorage.getItem("auth_token");
    try {
      const response = await fetch(`${API_URL}/api/admin/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({
          to_email: selectedUser.email,
          to_name: selectedUser.name,
          subject: emailForm.subject,
          message: emailForm.message
        })
      });
      if (!response.ok) throw new Error("Failed to send email");
      toast.success(`Email sent to ${selectedUser.name}`);
      setEmailDialogOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  // Document/Task Request function - Updated to use new admin tasks API
  const handleDocumentRequest = async () => {
    if (!docRequestForm.user_id || !docRequestForm.title) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSendingDocRequest(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      // Use the new admin tasks API
      const taskData = {
        user_id: docRequestForm.user_id,
        user_email: docRequestForm.user_email,
        task_type: docRequestForm.task_type,
        title: docRequestForm.title,
        description: docRequestForm.description,
        due_date: docRequestForm.due_date || null,
        priority: docRequestForm.priority,
        document_category: docRequestForm.category
      };
      
      const res = await fetch(`${API_URL}/api/admin/tasks/create`, {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(taskData)
      });
      
      if (res.ok) {
        const taskTypeLabel = {
          document_upload: "Document request",
          form_completion: "Form completion request",
          review_acknowledge: "Review request",
          information_request: "Information request",
          custom: "Task"
        }[docRequestForm.task_type] || "Task";
        
        toast.success(`${taskTypeLabel} sent successfully! User will be notified.`);
        setShowDocRequestModal(false);
        setDocRequestForm({
          user_id: "",
          user_email: "",
          task_type: "document_upload",
          category: "bank_statements",
          title: "",
          description: "",
          due_date: "",
          priority: "medium"
        });
        // Refresh admin tasks
        fetchAdminTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send request");
      }
    } catch (err) {
      toast.error("Failed to send request");
    } finally {
      setSendingDocRequest(false);
    }
  };

  // Fetch admin tasks
  const fetchAdminTasks = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/tasks/all`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setAdminTasks(data);
      }
    } catch (err) {
      console.error("Failed to fetch admin tasks:", err);
    }
  };

  // DocuHub Admin functions
  const fetchDocuhubUserData = async (userId) => {
    setDocuhubLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/docuhub/user/${userId}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setDocuhubUserData(data);
        setSelectedDocuhubUser(data.user);
        setShowDocuhubModal(true);
      }
    } catch (err) {
      toast.error("Failed to load user DocuHub");
    } finally {
      setDocuhubLoading(false);
    }
  };

  const fetchDocuhubComments = async (documentId) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/docuhub/documents/${documentId}/comments`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setDocuhubComments(data);
      }
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    }
  };

  const handleDocuhubComment = async () => {
    if (!selectedDocuhubDoc || !docuhubComment.trim()) return;
    setSendingDocuhubComment(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/docuhub/documents/${selectedDocuhubDoc.document_id}/comment`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ comment: docuhubComment, notify_user: true })
      });
      if (res.ok) {
        toast.success("Comment added and user notified");
        setDocuhubComment("");
        fetchDocuhubComments(selectedDocuhubDoc.document_id);
      }
    } catch (err) {
      toast.error("Failed to add comment");
    } finally {
      setSendingDocuhubComment(false);
    }
  };

  const handleMarkReviewed = async (documentId, isReviewed) => {
    setMarkingReviewed(true);
    const token = localStorage.getItem("auth_token");
    try {
      const endpoint = isReviewed ? "unreviewed" : "review";
      const res = await fetch(`${API_URL}/api/admin/docuhub/documents/${documentId}/${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        toast.success(isReviewed ? "Marked as needs review" : "Marked as reviewed");
        // Refresh the DocuHub data
        if (selectedDocuhubUser?.user_id) {
          // Find the user_id from docuhubUsers
          const userData = docuhubUsers.find(u => u.user_id === selectedDocuhubUser.user_id || 
            u.name === selectedDocuhubUser.name);
          if (userData) {
            await fetchDocuhubUserData(userData.user_id);
          }
        }
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to update review status");
    } finally {
      setMarkingReviewed(false);
    }
  };

  const getDocuhubFileUrl = (doc) => {
    if (!doc?.document_id) return null;
    const token = localStorage.getItem("auth_token");
    return `${API_URL}/api/docuhub/documents/${doc.document_id}/view?token=${token}`;
  };

  const pendingDocuhubCount = docuhubUsers.reduce((acc, u) => acc + (u.pending_count || 0), 0);

  // Payment Link functions
  const handleCreatePaymentLink = async (sendEmail = false) => {
    if (!paymentLinkForm.client_name || !paymentLinkForm.client_email || !paymentLinkForm.description || !paymentLinkForm.amount) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    const amount = parseFloat(paymentLinkForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setCreatingPaymentLink(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const res = await fetch(`${API_URL}/api/payment-links/create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          ...paymentLinkForm,
          amount: amount,
          send_email: sendEmail
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (sendEmail) {
          toast.success("Payment link created and sent to client!");
        } else {
          // Copy to clipboard
          await navigator.clipboard.writeText(data.stripe_url);
          toast.success("Payment link created and copied to clipboard!");
        }
        setShowPaymentLinkModal(false);
        setPaymentLinkForm({
          client_name: "",
          client_email: "",
          description: "",
          amount: "",
          notes: "",
          send_email: false
        });
        fetchData();
      } else {
        toast.error(data.detail || "Failed to create payment link");
      }
    } catch (err) {
      console.error("Error creating payment link:", err);
      toast.error("Failed to create payment link");
    } finally {
      setCreatingPaymentLink(false);
    }
  };

  const handleSendPaymentLinkEmail = async (paymentLinkId) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/payment-links/${paymentLinkId}/send-email`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        toast.success("Payment reminder sent!");
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to send email");
      }
    } catch (err) {
      toast.error("Failed to send email");
    }
  };

  const handleCopyPaymentLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const handleCheckPaymentStatus = async (paymentLinkId) => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/payment-links/${paymentLinkId}/check-status`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const data = await res.json();
      if (data.status === "paid") {
        toast.success("Payment completed!");
        fetchData();
      } else {
        toast.info("Payment still pending");
      }
    } catch (err) {
      toast.error("Failed to check status");
    }
  };

  const handleDeactivatePaymentLink = async (paymentLinkId) => {
    if (!window.confirm("Are you sure you want to deactivate this payment link?")) return;
    
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/payment-links/${paymentLinkId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        toast.success("Payment link deactivated");
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to deactivate");
      }
    } catch (err) {
      toast.error("Failed to deactivate payment link");
    }
  };

  const handleSelectRecipient = (recipient) => {
    setPaymentLinkForm(prev => ({
      ...prev,
      client_name: recipient.name,
      client_email: recipient.email
    }));
  };

  const filteredPaymentLinks = paymentLinks.filter(pl => {
    if (paymentLinkFilter === "all") return true;
    return pl.status === paymentLinkFilter;
  });

  const paymentLinkStats = {
    total: paymentLinks.length,
    pending: paymentLinks.filter(pl => pl.status === "pending").length,
    paid: paymentLinks.filter(pl => pl.status === "paid").length,
    totalPaid: paymentLinks.filter(pl => pl.status === "paid").reduce((sum, pl) => sum + pl.amount, 0),
    totalPending: paymentLinks.filter(pl => pl.status === "pending").reduce((sum, pl) => sum + pl.amount, 0)
  };

  const getPlanBadge = (plan) => {
    const p = PLANS.find(x => x.id === plan) || PLANS[0];
    const colors = { gray: "bg-gray-100 text-gray-700", blue: "bg-blue-100 text-blue-700", green: "bg-green-100 text-green-700", purple: "bg-purple-100 text-purple-700", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-700" };
    return <Badge className={`rounded-full ${colors[p.color]}`}>{p.name}</Badge>;
  };

  const getRoleBadge = (role) => {
    const r = ROLES.find(x => x.id === role) || ROLES[0];
    const colors = { gray: "bg-gray-100 text-gray-700", blue: "bg-blue-100 text-blue-700", purple: "bg-purple-100 text-purple-700", red: "bg-[#121212] text-white" };
    return <Badge className={`rounded-full ${colors[r.color]}`}>{r.name}</Badge>;
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const filteredUsers = users.filter(u => u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredClients = clients.filter(c => c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredInvoices = invoices.filter(i => i.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) || i.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredDocuments = documents.filter(d => d.filename?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredContracts = contracts.filter(c => c.title?.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7FFE5]">
      <div className="w-10 h-10 border-4 border-[#BFFF00] border-t-[#121212] rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white font-['Inter']" data-testid={isViewOnly ? "finance-dashboard" : "admin-dashboard"}>
      {/* View-Only Banner for Finance Users */}
      {isViewOnly && (
        <div className="bg-[#BFFF00]/20 border-b border-[#BFFF00]/50 py-2.5 px-4 flex items-center justify-center gap-2">
          <Eye className="w-4 h-4 text-[#121212]" />
          <p className="text-sm font-medium text-[#121212]">
            View-Only Mode — You can view all data but cannot make changes
          </p>
        </div>
      )}
      
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`lg:hidden fixed left-4 z-50 p-2.5 bg-[#121212] rounded-xl text-white shadow-lg ${isViewOnly ? 'top-14' : 'top-4'}`}
        data-testid="admin-mobile-menu-btn"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-30" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Lime Green Sidebar */}
      <aside 
        className={`fixed left-0 w-64 z-40 transform transition-transform duration-300 ease-in-out flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{ 
          top: isViewOnly ? '44px' : 0,
          height: isViewOnly ? 'calc(100vh - 44px)' : '100vh',
          background: 'linear-gradient(180deg, #F7FFE5 0%, #EDFFC2 100%)' 
        }}
      >
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: 'linear-gradient(#BFFF00 1px, transparent 1px), linear-gradient(90deg, #BFFF00 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        
        {/* Logo */}
        <div className="relative p-6 border-b border-[#BFFF00]/30">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#BFFF00] rounded-xl flex items-center justify-center shadow-lg shadow-[#BFFF00]/30">
              <span className="text-[#121212] font-extrabold text-lg font-['Manrope']">F</span>
            </div>
            <span className="text-[#121212] font-extrabold text-xl tracking-tight font-['Manrope']">
              {isViewOnly ? "Finance" : "Admin"}
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {NAV_ITEMS.filter(item => !item.adminOnly || user?.role === "admin").map(item => {
              const isActive = activeTab === item.id;
              const badgeCount = item.id === "quotes" ? quotes.length + contacts.length : 
                                 item.id === "invoices" ? invoices.length :
                                 item.id === "documents" ? documents.length :
                                 item.id === "contracts" ? contracts.length :
                                 item.id === "reviews" ? pendingReviewCount :
                                 item.id === "payouts" ? pendingPayouts.length : 0;
              return (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive ? 'bg-[#121212] text-white shadow-lg' : 'text-[#121212]/70 hover:text-[#121212] hover:bg-[#BFFF00]/40'
                  }`}
                  data-testid={`admin-nav-${item.id}`}
                >
                  <item.icon className={`w-5 h-5 ${isActive ? 'text-[#BFFF00]' : ''}`} strokeWidth={1.5} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {badgeCount > 0 && (
                    <Badge className={`${isActive ? 'bg-[#BFFF00] text-[#121212]' : item.id === "reviews" ? 'bg-amber-500 text-white animate-pulse' : 'bg-[#121212]/10 text-[#121212]'} rounded-full`}>
                      {badgeCount}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* User Profile */}
        <div className="relative p-4 border-t border-[#BFFF00]/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#121212] rounded-xl flex items-center justify-center text-[#BFFF00] font-bold text-sm">
              {user?.name?.[0] || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#121212] truncate">{user?.name}</p>
              <Badge className={`text-xs rounded-full ${isViewOnly ? 'bg-[#BFFF00]/30 text-[#121212]' : 'bg-[#121212] text-[#BFFF00]'}`}>
                {isViewOnly ? "Finance" : "Admin"}
              </Badge>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={handleLogout} 
            className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-[#BFFF00]/20 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="ml-12 lg:ml-0">
              <h1 className="text-xl font-bold text-[#121212] font-['Manrope']">
                {isViewOnly ? "Finance Dashboard" : "Admin Dashboard"}
              </h1>
              <p className="text-sm text-[#121212]/60">
                {isViewOnly ? "View-only access to platform data" : "Master access to all platform data"}
              </p>
            </div>
            <div className="relative w-64 hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#121212]/40" />
              <Input 
                placeholder="Search..." 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)} 
                className="pl-9 h-10 bg-[#F7FFE5] border-[#BFFF00]/30 rounded-xl focus:ring-2 focus:ring-[#BFFF00]/50"
              />
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: "Total Users", value: stats?.total_users || users.length, icon: Users, color: "blue" },
                  { label: "Total Invoices", value: invoices.length, icon: Receipt, color: "green" },
                  { label: "Total Documents", value: documents.length, icon: FolderOpen, color: "purple" },
                  { label: "Total Contracts", value: contracts.length, icon: FileSignature, color: "amber" }
                ].map((stat, idx) => (
                  <Card key={idx} className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl bg-white hover:border-[#BFFF00] transition-colors">
                    <CardContent className="pt-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-[#121212]/60">{stat.label}</span>
                        <div className="w-8 h-8 bg-[#F7FFE5] rounded-xl flex items-center justify-center">
                          <stat.icon className="w-4 h-4 text-[#121212]" />
                        </div>
                      </div>
                      <p className="text-3xl font-bold text-[#121212] font-['Manrope']">{stat.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Recent Activity */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base font-bold font-['Manrope']">Recent Users</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab("users")} className="text-[#121212] hover:bg-[#BFFF00]/20">
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {users.slice(0, 5).map(u => (
                      <div key={u.user_id} className="flex items-center justify-between py-3 border-b border-[#BFFF00]/10 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#F7FFE5] rounded-xl flex items-center justify-center">
                            <User className="w-4 h-4 text-[#121212]" />
                          </div>
                          <div>
                            <p className="font-medium text-[#121212]">{u.name}</p>
                            <p className="text-xs text-[#121212]/50">{u.email}</p>
                          </div>
                        </div>
                        {getRoleBadge(u.role)}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base font-bold font-['Manrope']">Recent Invoices</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab("invoices")} className="text-[#121212] hover:bg-[#BFFF00]/20">
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {invoices.length === 0 ? (
                      <p className="text-center text-[#121212]/40 py-4">No invoices yet</p>
                    ) : (
                      invoices.slice(0, 5).map(inv => (
                        <div key={inv.invoice_id} className="flex items-center justify-between py-3 border-b border-[#BFFF00]/10 last:border-0">
                          <div>
                            <p className="font-medium text-[#121212]">#{inv.invoice_number || inv.invoice_id?.slice(-6)}</p>
                            <p className="text-xs text-[#121212]/50">{inv.client_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#121212]">{formatCurrency(inv.amount)}</p>
                            <Badge className={`rounded-full ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                              {inv.status}
                            </Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
              <CardHeader>
                <CardTitle className="font-bold font-['Manrope']">All Users ({filteredUsers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-[#F7FFE5] border-b border-[#BFFF00]/20">
                      <tr>
                        <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">User</th>
                        <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Role</th>
                        <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Plan</th>
                        {!isViewOnly && <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#BFFF00]/10">
                      {filteredUsers.map(u => (
                        <tr key={u.user_id} className="hover:bg-[#F7FFE5]/50" data-testid={`user-row-${u.user_id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {u.picture ? <img src={u.picture} alt={u.name} className="w-9 h-9 rounded-xl" /> : <div className="w-9 h-9 bg-[#F7FFE5] rounded-xl flex items-center justify-center"><User className="w-4 h-4 text-[#121212]" /></div>}
                              <div>
                                <p className="font-medium text-[#121212]">{u.name}</p>
                                <p className="text-sm text-[#121212]/50">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">{getRoleBadge(u.role)}</td>
                          <td className="px-4 py-3">{getPlanBadge(u.plan)}</td>
                          {!isViewOnly && (
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button variant="outline" size="sm" onClick={() => openUserEditModal(u)} className="rounded-full border-[#121212]/20 hover:bg-[#121212] hover:text-white" data-testid={`edit-btn-${u.user_id}`}>
                                  <Eye className="w-3 h-3 mr-1" /> Edit
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => openLimitsModal(u)} className="rounded-full border-purple-300 hover:bg-purple-100" data-testid={`limits-btn-${u.user_id}`}>
                                  <TrendingUp className="w-3 h-3 mr-1" /> Limits
                                </Button>
                                {/* Role and Plan buttons only for admin - finance_admin cannot manage users */}
                                {user?.role === "admin" && (
                                  <>
                                    <Button variant="outline" size="sm" onClick={() => openRoleDialog(u)} className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20" data-testid={`role-btn-${u.user_id}`}>
                                      <Shield className="w-3 h-3 mr-1" /> Role
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => openPlanDialog(u)} className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20" data-testid={`upgrade-btn-${u.user_id}`}>
                                      <Crown className="w-3 h-3 mr-1" /> Plan
                                    </Button>
                                  </>
                                )}
                                <Button variant="outline" size="sm" onClick={() => openEmailDialog(u)} className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20" data-testid={`email-btn-${u.user_id}`}>
                                  <Mail className="w-3 h-3 mr-1" /> Email
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bookkeepers Tab (Admin Only) */}
          {activeTab === "bookkeepers" && user?.role === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold font-['Manrope']">Bookkeeper Assignments</h2>
                  <p className="text-sm text-gray-500">Manage which users are assigned to each bookkeeper</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Bookkeepers List */}
                <Card className="border-2 border-dashed border-blue-300 rounded-2xl lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="text-lg font-bold font-['Manrope'] flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Bookkeepers ({bookkeepers.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {bookkeepersLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                      </div>
                    ) : bookkeepers.length === 0 ? (
                      <div className="text-center py-8">
                        <Shield className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500 text-sm">No bookkeepers yet</p>
                        <p className="text-gray-400 text-xs mt-1">Assign "Bookkeeper" role to users to add them here</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {bookkeepers.map((bk) => (
                          <button
                            key={bk.user_id}
                            onClick={() => {
                              setSelectedBookkeeper(bk);
                              fetchBookkeeperAssignments(bk.user_id);
                            }}
                            className={`w-full text-left p-3 rounded-xl transition-all ${
                              selectedBookkeeper?.user_id === bk.user_id
                                ? 'bg-blue-100 border-2 border-blue-400'
                                : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                            }`}
                            data-testid={`bookkeeper-${bk.user_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{bk.name}</p>
                                <p className="text-xs text-gray-500">{bk.email}</p>
                              </div>
                              <Badge className="bg-blue-100 text-blue-700">
                                {bk.assigned_count || 0} users
                              </Badge>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Assignments for Selected Bookkeeper */}
                <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl lg:col-span-2">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold font-['Manrope']">
                        {selectedBookkeeper ? `Assigned to ${selectedBookkeeper.name}` : 'Select a Bookkeeper'}
                      </CardTitle>
                      {selectedBookkeeper && (
                        <Button
                          onClick={() => setShowAssignModal(true)}
                          className="bg-[#BFFF00] text-[#121212] hover:bg-[#a8e600] rounded-full"
                          data-testid="assign-user-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Assign User
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {!selectedBookkeeper ? (
                      <div className="text-center py-12">
                        <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">Select a bookkeeper to view their assigned users</p>
                      </div>
                    ) : bookkeeperAssignments.length === 0 ? (
                      <div className="text-center py-12">
                        <User className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No users assigned yet</p>
                        <Button
                          onClick={() => setShowAssignModal(true)}
                          variant="outline"
                          className="mt-4 rounded-full"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Assign First User
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {bookkeeperAssignments.map((assignment) => (
                          <div
                            key={assignment.user_id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100"
                            data-testid={`assignment-${assignment.user_id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center">
                                <User className="w-5 h-5 text-[#121212]" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{assignment.user?.name}</p>
                                <p className="text-sm text-gray-500">{assignment.user?.email}</p>
                                <p className="text-xs text-gray-400">
                                  Plan: {assignment.user?.plan || 'Free'} • Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnassignUser(assignment.user_id)}
                              className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                              data-testid={`unassign-${assignment.user_id}`}
                            >
                              <X className="w-3 h-3 mr-1" /> Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Review Tasks Tab */}
          {activeTab === "reviews" && (
            <div className="space-y-6">
              {/* Review Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <ClipboardCheck className="w-4 h-4 text-amber-600" />
                      <p className="text-sm text-amber-700 font-medium">Pending Reviews</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-700 font-['Manrope']">{pendingReviewCount}</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-green-300 bg-green-50 rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <p className="text-sm text-green-700 font-medium">Completed</p>
                    </div>
                    <p className="text-2xl font-bold text-green-700 font-['Manrope']">
                      {reviewTasks.filter(t => t.status === "completed").length}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-[#BFFF00]/50 bg-[#F7FFE5] rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <HelpCircle className="w-4 h-4 text-[#121212]" />
                      <p className="text-sm text-[#121212]/70 font-medium">Total Suspense Items</p>
                    </div>
                    <p className="text-2xl font-bold text-[#121212] font-['Manrope']">
                      {reviewTasks.filter(t => t.status === "pending").reduce((sum, t) => sum + (t.suspense_count || 0), 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Review Tasks List */}
              <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                <CardHeader>
                  <CardTitle className="font-bold font-['Manrope']">Document Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  {reviewTasks.length === 0 ? (
                    <div className="text-center py-12 text-[#121212]/40">
                      <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No documents submitted for review yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reviewTasks.map(task => (
                        <div 
                          key={task.task_id}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                            task.status === "pending" 
                              ? 'border-amber-300 bg-amber-50 hover:border-amber-400' 
                              : 'border-green-300 bg-green-50'
                          }`}
                          onClick={() => {
                            setSelectedReviewTask(task);
                            fetchReviewTaskDetails(task.task_id);
                          }}
                          data-testid={`review-task-${task.task_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-bold text-[#121212]">{task.document_name}</p>
                                <Badge className={`rounded-full ${task.status === "pending" ? 'bg-amber-500 text-white' : 'bg-green-500 text-white'}`}>
                                  {task.status}
                                </Badge>
                                {task.suspense_count > 0 && (
                                  <Badge className="bg-red-100 text-red-700 rounded-full">
                                    {task.suspense_count} suspense
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-[#121212]/60 mb-2">
                                Submitted by <span className="font-medium">{task.user_name || task.user_email}</span>
                              </p>
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-green-700">
                                  <TrendingUp className="w-4 h-4 inline mr-1" />
                                  {formatCurrency(task.total_income || 0)}
                                </span>
                                <span className="text-red-700">
                                  <TrendingDown className="w-4 h-4 inline mr-1" />
                                  {formatCurrency(task.total_expenses || 0)}
                                </span>
                                <span className="text-[#121212]/60">
                                  {task.transaction_count || 0} transactions
                                </span>
                              </div>
                            </div>
                            <div className="text-right text-sm text-[#121212]/50">
                              <p>{new Date(task.created_at).toLocaleDateString()}</p>
                              {task.status === "completed" && task.reviewer_name && (
                                <p className="text-green-700 mt-1">
                                  <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                  {task.reviewer_name}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Invoices Tab - All Users' Invoices */}
          {activeTab === "invoices" && (
            <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
              <CardHeader>
                <CardTitle className="font-bold font-['Manrope']">All Invoices ({filteredInvoices.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <div className="text-center py-12 text-[#121212]/40">
                    <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No invoices in the system yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                      <thead className="bg-[#F7FFE5] border-b border-[#BFFF00]/20">
                        <tr>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Invoice</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Owner</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Client</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Amount</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#BFFF00]/10">
                        {filteredInvoices.map(inv => (
                          <tr key={inv.invoice_id} className="hover:bg-[#F7FFE5]/50">
                            <td className="px-4 py-3 font-medium">#{inv.invoice_number || inv.invoice_id?.slice(-6)}</td>
                            <td className="px-4 py-3 text-[#121212]/70">{inv.user_email || "Unknown"}</td>
                            <td className="px-4 py-3 text-[#121212]/70">{inv.client_name}</td>
                            <td className="px-4 py-3 font-bold">{formatCurrency(inv.amount)}</td>
                            <td className="px-4 py-3">
                              <Badge className={`rounded-full ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                {inv.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Documents Tab - User-Centric View */}
          {activeTab === "documents" && (
            <div className="space-y-6">
              {/* Header with Actions */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#121212] font-['Manrope']">Document Management</h2>
                <div className="flex items-center gap-2">
                  {!isViewOnly && (
                    <Button 
                      onClick={() => setShowDocRequestModal(true)}
                      className="bg-[#121212] hover:bg-[#2a2a2a] gap-2 rounded-full px-6"
                      data-testid="request-document-btn"
                    >
                      <Bell className="w-4 h-4" />
                      Request Document
                    </Button>
                  )}
                </div>
              </div>

              {/* Combined Stats */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card className="border-2 border-dashed border-blue-300 bg-blue-50 rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-blue-600" />
                      <p className="text-sm text-blue-700 font-medium">Users</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-700 font-['Manrope']">{Math.max(usersWithDocs.length, docuhubUsers.length)}</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-[#BFFF00]/50 bg-[#F7FFE5] rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <FolderOpen className="w-4 h-4 text-[#121212]" />
                      <p className="text-sm text-[#121212]/70 font-medium">AI Docs</p>
                    </div>
                    <p className="text-2xl font-bold text-[#121212] font-['Manrope']">{documents.length}</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-purple-300 bg-purple-50 rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <FolderArchive className="w-4 h-4 text-purple-600" />
                      <p className="text-sm text-purple-700 font-medium">DocuHub</p>
                    </div>
                    <p className="text-2xl font-bold text-purple-700 font-['Manrope']">
                      {docuhubUsers.reduce((acc, u) => acc + (u.total_documents || 0), 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <p className="text-sm text-amber-700 font-medium">Pending</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-700 font-['Manrope']">{pendingDocuhubCount}</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-green-300 bg-green-50 rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <p className="text-sm text-green-700 font-medium">Reviewed</p>
                    </div>
                    <p className="text-2xl font-bold text-green-700 font-['Manrope']">
                      {docuhubUsers.reduce((acc, u) => acc + (u.reviewed_count || 0), 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Tabbed View for AI Documents and DocuHub */}
              <Tabs defaultValue="docuhub" className="w-full">
                <TabsList className="bg-[#F7FFE5] border border-[#BFFF00]/30 rounded-full p-1">
                  <TabsTrigger value="docuhub" className="rounded-full data-[state=active]:bg-[#121212] data-[state=active]:text-white gap-2">
                    <FolderArchive className="w-4 h-4" />
                    DocuHub ({docuhubUsers.reduce((acc, u) => acc + (u.total_documents || 0), 0)})
                  </TabsTrigger>
                  <TabsTrigger value="ai-analyzed" className="rounded-full data-[state=active]:bg-[#121212] data-[state=active]:text-white gap-2">
                    <Sparkles className="w-4 h-4" />
                    AI Analyzed ({documents.length})
                  </TabsTrigger>
                </TabsList>

                {/* DocuHub Tab Content */}
                <TabsContent value="docuhub" className="mt-4">
                  <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-bold font-['Manrope']">User DocuHubs</CardTitle>
                      {docuhubUsers.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            toast.info("Preparing download...");
                            // In production, this would call a backend endpoint to create a zip
                            window.open(`${API_URL}/api/admin/docuhub/download-all?token=${localStorage.getItem("auth_token")}`, '_blank');
                          }}
                          className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20 gap-2"
                          data-testid="download-all-docuhub-btn"
                        >
                          <Download className="w-4 h-4" />
                          Download All
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      {docuhubUsers.length === 0 ? (
                        <div className="text-center py-12 text-[#121212]/40">
                          <FolderArchive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No users have uploaded documents to DocuHub yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {docuhubUsers.map(userDoc => (
                            <div 
                              key={userDoc.user_id}
                              className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                                userDoc.pending_count > 0 
                                  ? 'border-amber-300 bg-amber-50 hover:border-amber-400' 
                                  : 'border-green-300 bg-green-50 hover:border-green-400'
                              }`}
                              onClick={() => fetchDocuhubUserData(userDoc.user_id)}
                              data-testid={`docuhub-user-${userDoc.user_id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-bold text-[#121212]">{userDoc.name || userDoc.email}</p>
                                    {userDoc.pending_count > 0 && (
                                      <Badge className="bg-amber-500 text-white rounded-full">
                                        {userDoc.pending_count} pending
                                      </Badge>
                                    )}
                                    {userDoc.reviewed_count > 0 && (
                                      <Badge className="bg-green-100 text-green-700 rounded-full">
                                        {userDoc.reviewed_count} reviewed
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-[#121212]/60 mb-2">{userDoc.email}</p>
                                  {userDoc.company_name && (
                                    <p className="text-sm text-[#121212]/50">{userDoc.company_name}</p>
                                  )}
                                  <div className="flex items-center gap-4 text-sm mt-2">
                                    <span className="text-[#121212]/60">
                                      <FileText className="w-4 h-4 inline mr-1" />
                                      {userDoc.total_documents} documents
                                    </span>
                                    <span className="text-[#121212]/60">
                                      <FolderOpen className="w-4 h-4 inline mr-1" />
                                      {userDoc.folders_used} folders
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right text-sm text-[#121212]/50">
                                  <p>Latest upload:</p>
                                  <p>{userDoc.latest_upload ? new Date(userDoc.latest_upload).toLocaleDateString() : '-'}</p>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="mt-2 text-[#121212] hover:bg-[#BFFF00]/20"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    View Docs
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* AI Analyzed Documents Tab Content */}
                <TabsContent value="ai-analyzed" className="mt-4">
                  <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-bold font-['Manrope']">AI Analyzed Documents</CardTitle>
                      {usersWithDocs.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            toast.info("Preparing download...");
                            window.open(`${API_URL}/api/admin/documents/download-all?token=${localStorage.getItem("auth_token")}`, '_blank');
                          }}
                          className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20 gap-2"
                          data-testid="download-all-docs-btn"
                        >
                          <Download className="w-4 h-4" />
                          Download All
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      {usersWithDocs.length === 0 ? (
                        <div className="text-center py-12 text-[#121212]/40">
                          <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No users have uploaded documents for AI analysis yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {usersWithDocs.map(u => (
                            <div 
                              key={u.user_id}
                              className="p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#BFFF00] cursor-pointer transition-all hover:shadow-md bg-white"
                              onClick={() => fetchUserDocuments(u.user_id)}
                              data-testid={`user-docs-${u.user_id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center text-[#121212] font-bold">
                                    {u.name?.[0] || u.email?.[0] || "U"}
                                  </div>
                                  <div>
                                    <p className="font-bold text-[#121212]">{u.name || "Unknown"}</p>
                                    <p className="text-sm text-[#121212]/60">{u.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <Badge className="bg-[#BFFF00]/30 text-[#121212] rounded-full">
                                      {u.document_count} {u.document_count === 1 ? 'document' : 'documents'}
                                    </Badge>
                                    {u.last_updated && (
                                      <p className="text-xs text-[#121212]/50 mt-1">
                                        Last updated: {new Date(u.last_updated).toLocaleDateString()}
                                      </p>
                                    )}
                                  </div>
                                  <ChevronRight className="w-5 h-5 text-[#121212]/40" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Contracts Tab - All Users' Contracts */}
          {activeTab === "contracts" && (
            <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
              <CardHeader>
                <CardTitle className="font-bold font-['Manrope']">All Contracts ({filteredContracts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {contracts.length === 0 ? (
                  <div className="text-center py-12 text-[#121212]/40">
                    <FileSignature className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No contracts in the system yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-[#F7FFE5] border-b border-[#BFFF00]/20">
                        <tr>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Contract</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Owner</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Type</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#BFFF00]/10">
                        {filteredContracts.map(contract => (
                          <tr key={contract.contract_id} className="hover:bg-[#F7FFE5]/50">
                            <td className="px-4 py-3 font-medium">{contract.title}</td>
                            <td className="px-4 py-3 text-[#121212]/70">{contract.user_email || "Unknown"}</td>
                            <td className="px-4 py-3">
                              <Badge className="rounded-full bg-[#F7FFE5] text-[#121212]">{contract.template_type || "Custom"}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`rounded-full ${contract.status === 'signed' ? 'bg-green-100 text-green-700' : contract.status === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                {contract.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tax Forms Tab */}
          {activeTab === "taxforms" && (
            <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
              <CardHeader>
                <CardTitle className="font-bold font-['Manrope']">All Tax Forms ({taxForms.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {taxForms.length === 0 ? (
                  <div className="text-center py-12 text-[#121212]/40">
                    <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No tax forms in the system yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                      <thead className="bg-[#F7FFE5] border-b border-[#BFFF00]/20">
                        <tr>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Form Type</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Owner</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Status</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Created</th>
                          <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#BFFF00]/10">
                        {taxForms.map(form => (
                          <tr key={form.form_id} className="hover:bg-[#F7FFE5]/50">
                            <td className="px-4 py-3 font-medium">{form.form_type}</td>
                            <td className="px-4 py-3 text-[#121212]/70">{form.user_email || "Unknown"}</td>
                            <td className="px-4 py-3">
                              <Badge className={`rounded-full ${form.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {form.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-[#121212]/70">{form.created_at ? new Date(form.created_at).toLocaleDateString() : "-"}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {form.pdf_filename && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleQuickLook({
                                        filename: form.pdf_filename,
                                        file_type: 'pdf',
                                        file_url: `/api/tax-forms/download/${form.form_id}`,
                                        created_at: form.created_at
                                      })}
                                      className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
                                      data-testid={`taxform-quicklook-${form.form_id}`}
                                    >
                                      <Eye className="w-4 h-4 mr-1" /> Quick Look
                                    </Button>
                                    <a 
                                      href={`${API_URL}/api/tax-forms/download/${form.form_id}?token=${localStorage.getItem("auth_token")}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-[#121212] text-white rounded-full hover:bg-[#2a2a2a]"
                                    >
                                      <Download className="w-3 h-3" />
                                    </a>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Clients Tab */}
          {activeTab === "clients" && (
            <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
              <CardHeader>
                <CardTitle className="font-bold font-['Manrope']">All Clients ({filteredClients.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredClients.length === 0 ? (
                  <div className="text-center py-12 text-[#121212]/40">
                    <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No clients yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredClients.map(c => (
                      <div key={c.client_id} className="flex items-center justify-between p-4 bg-[#F7FFE5]/50 rounded-2xl hover:bg-[#BFFF00]/20 transition-colors">
                        <div>
                          <p className="font-bold text-[#121212]">{c.company_name}</p>
                          <p className="text-sm text-[#121212]/60">{c.contact_email} • {c.entity_type}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getPlanBadge(c.plan)}
                          <Badge variant="outline" className="rounded-full capitalize">{c.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Links Tab */}
          {activeTab === "payment-links" && (
            <div className="space-y-6" data-testid="payment-links-tab">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#BFFF00]/30 flex items-center justify-center">
                        <Link2 className="w-5 h-5 text-[#121212]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#121212]">{paymentLinkStats.total}</p>
                        <p className="text-xs text-[#121212]/60">Total Links</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-amber-200 rounded-2xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#121212]">{paymentLinkStats.pending}</p>
                        <p className="text-xs text-[#121212]/60">Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-emerald-200 rounded-2xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(paymentLinkStats.totalPaid)}</p>
                        <p className="text-xs text-[#121212]/60">Collected</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed border-blue-200 rounded-2xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{formatCurrency(paymentLinkStats.totalPending)}</p>
                        <p className="text-xs text-[#121212]/60">Outstanding</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Payment Links List */}
              <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-bold font-['Manrope']">Payment Links</CardTitle>
                  <div className="flex items-center gap-3">
                    <select
                      value={paymentLinkFilter}
                      onChange={(e) => setPaymentLinkFilter(e.target.value)}
                      className="px-3 py-2 border border-[#121212]/20 rounded-xl text-sm bg-white"
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="deactivated">Deactivated</option>
                    </select>
                    {!isViewOnly && (
                      <Button 
                        onClick={() => setShowPaymentLinkModal(true)}
                        className="bg-[#BFFF00] hover:bg-[#BFFF00]/80 text-[#121212] rounded-full"
                        data-testid="create-payment-link-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Link
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {filteredPaymentLinks.length === 0 ? (
                    <div className="text-center py-12">
                      <Link2 className="w-12 h-12 text-[#121212]/20 mx-auto mb-4" />
                      <p className="text-[#121212]/40">No payment links yet</p>
                      {!isViewOnly && (
                        <Button 
                          onClick={() => setShowPaymentLinkModal(true)}
                          variant="outline" 
                          className="mt-4 rounded-full"
                        >
                          Create your first payment link
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredPaymentLinks.map((pl) => (
                        <div 
                          key={pl.payment_link_id} 
                          className={`p-4 rounded-2xl border-2 ${
                            pl.status === "paid" 
                              ? "bg-emerald-50/50 border-emerald-200" 
                              : pl.status === "deactivated"
                              ? "bg-gray-50 border-gray-200"
                              : "bg-[#F7FFE5]/50 border-[#BFFF00]/30"
                          }`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-bold text-[#121212]">{pl.client_name}</p>
                                <Badge 
                                  className={`rounded-full text-xs ${
                                    pl.status === "paid" 
                                      ? "bg-emerald-100 text-emerald-700" 
                                      : pl.status === "deactivated"
                                      ? "bg-gray-100 text-gray-700"
                                      : "bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  {pl.status}
                                </Badge>
                                {pl.email_sent && (
                                  <Badge variant="outline" className="rounded-full text-xs">
                                    <Mail className="w-3 h-3 mr-1" />
                                    Sent
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-[#121212]/60">{pl.client_email}</p>
                              <p className="text-sm text-[#121212]/80 mt-1">{pl.description}</p>
                              {pl.notes && (
                                <p className="text-xs text-[#121212]/50 mt-1 italic">Note: {pl.notes}</p>
                              )}
                              <p className="text-xs text-[#121212]/40 mt-2">
                                Created {new Date(pl.created_at).toLocaleDateString()} by {pl.created_by_name}
                                {pl.paid_at && ` • Paid ${new Date(pl.paid_at).toLocaleDateString()}`}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <p className={`text-2xl font-bold ${
                                pl.status === "paid" ? "text-emerald-600" : "text-[#121212]"
                              }`}>
                                {formatCurrency(pl.amount)}
                              </p>
                              {pl.status === "pending" && !isViewOnly && (
                                <div className="flex items-center gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="rounded-full text-xs"
                                    onClick={() => handleCopyPaymentLink(pl.stripe_url)}
                                  >
                                    <Copy className="w-3 h-3 mr-1" />
                                    Copy
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="rounded-full text-xs"
                                    onClick={() => handleSendPaymentLinkEmail(pl.payment_link_id)}
                                  >
                                    <Mail className="w-3 h-3 mr-1" />
                                    Send
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="rounded-full text-xs"
                                    onClick={() => handleCheckPaymentStatus(pl.payment_link_id)}
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Check
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="rounded-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeactivatePaymentLink(pl.payment_link_id)}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Payouts Management Tab */}
          {activeTab === "payouts" && (
            <div className="space-y-6">
              {/* Platform Stats */}
              {masterData && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border border-green-200 bg-gradient-to-br from-green-50 to-white rounded-2xl">
                    <CardContent className="p-4">
                      <p className="text-xs text-green-600 font-medium">Total Collected</p>
                      <p className="text-2xl font-bold text-green-700">${masterData.stats.total_collected.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className="border border-[#BFFF00]/30 bg-gradient-to-br from-[#F7FFE5] to-white rounded-2xl">
                    <CardContent className="p-4">
                      <p className="text-xs text-[#121212]/60 font-medium">Platform Fees (1%)</p>
                      <p className="text-2xl font-bold text-[#121212]">${masterData.stats.total_platform_fees.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className="border border-amber-200 bg-gradient-to-br from-amber-50 to-white rounded-2xl">
                    <CardContent className="p-4">
                      <p className="text-xs text-amber-600 font-medium">Pending Payouts</p>
                      <p className="text-2xl font-bold text-amber-700">${masterData.stats.pending_payouts.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className="border border-blue-200 bg-gradient-to-br from-blue-50 to-white rounded-2xl">
                    <CardContent className="p-4">
                      <p className="text-xs text-blue-600 font-medium">Paid Out</p>
                      <p className="text-2xl font-bold text-blue-700">${masterData.stats.processed_payouts.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Payout Sub-tabs */}
              <Tabs value={payoutTab} onValueChange={setPayoutTab} className="w-full">
                <TabsList className="bg-[#F7FFE5] rounded-xl p-1">
                  <TabsTrigger value="pending" className="rounded-lg data-[state=active]:bg-white">
                    Pending Payouts
                    {pendingPayouts.length > 0 && (
                      <Badge className="ml-2 bg-amber-100 text-amber-700">{pendingPayouts.length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-white">
                    Payout History
                  </TabsTrigger>
                  <TabsTrigger value="auto-payout" className="rounded-lg data-[state=active]:bg-white">
                    Auto-Payout Users
                  </TabsTrigger>
                </TabsList>

                {/* Pending Payouts */}
                <TabsContent value="pending" className="mt-4">
                  <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-bold font-['Manrope']">Pending Payouts by User</CardTitle>
                      <Button variant="outline" size="sm" onClick={() => fetchPayoutData()} disabled={loadingPayouts}>
                        <RefreshCw className={`w-4 h-4 mr-1 ${loadingPayouts ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {loadingPayouts ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-8 h-8 animate-spin text-[#121212]/40" />
                        </div>
                      ) : pendingPayouts.length === 0 ? (
                        <div className="text-center py-12">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                          <p className="text-[#121212]/60">No pending payouts</p>
                          <p className="text-sm text-[#121212]/40">All payouts have been processed</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {pendingPayouts.map((payout) => (
                            <div 
                              key={payout.user_id}
                              className="p-4 border border-gray-200 rounded-xl hover:border-[#BFFF00] transition-colors"
                            >
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <User className="w-5 h-5 text-[#121212]/40" />
                                    <span className="font-semibold text-[#121212]">{payout.user_name}</span>
                                    {!payout.has_bank_details && (
                                      <Badge className="bg-red-100 text-red-700 text-xs">No Bank Details</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-[#121212]/60 mt-1">{payout.user_email}</p>
                                  <div className="flex flex-wrap gap-4 mt-2 text-sm">
                                    <span className="text-gray-500">
                                      <strong>{payout.link_count}</strong> payment links
                                    </span>
                                    <span className="text-green-600">
                                      Total: <strong>${payout.total_amount.toFixed(2)}</strong>
                                    </span>
                                    <span className="text-[#121212]/60">
                                      Platform Fee: <strong>${payout.platform_fees.toFixed(2)}</strong>
                                    </span>
                                  </div>
                                  {payout.bank_details && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Bank: {payout.bank_details.bank_name} •••• {payout.bank_details.account_number?.slice(-4)}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <div className="text-right">
                                    <p className="text-xs text-gray-500">Payout Amount</p>
                                    <p className="text-2xl font-bold text-[#121212]">${payout.payout_amount.toFixed(2)}</p>
                                  </div>
                                  <Button
                                    onClick={() => handleProcessPayout(payout)}
                                    disabled={processingPayout === payout.user_id || !payout.has_bank_details}
                                    className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full"
                                  >
                                    {processingPayout === payout.user_id ? (
                                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                                    ) : (
                                      <><CheckCircle className="w-4 h-4 mr-2" /> Process Payout</>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Payout History */}
                <TabsContent value="history" className="mt-4">
                  <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                    <CardHeader>
                      <CardTitle className="font-bold font-['Manrope']">Payout History ({payoutHistory.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {payoutHistory.length === 0 ? (
                        <p className="text-center text-[#121212]/40 py-8">No payout history yet</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Payout ID</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">User</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Platform Fee</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payoutHistory.map((payout) => (
                                <tr key={payout.payout_id} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="py-3 px-4 font-mono text-sm">{payout.payout_id.slice(0, 12)}...</td>
                                  <td className="py-3 px-4 text-sm">{payout.user_id.slice(0, 8)}...</td>
                                  <td className="py-3 px-4">
                                    <span className="font-semibold text-[#121212]">${payout.payout_amount?.toFixed(2)}</span>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-500">${payout.platform_fees?.toFixed(2)}</td>
                                  <td className="py-3 px-4">
                                    <Badge className={
                                      payout.status === "transferred" ? "bg-green-100 text-green-700" :
                                      payout.status === "processed" ? "bg-blue-100 text-blue-700" :
                                      "bg-gray-100 text-gray-700"
                                    }>
                                      {payout.status === "transferred" && <CheckCircle className="w-3 h-3 mr-1" />}
                                      {payout.status}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-500">
                                    {payout.created_at ? new Date(payout.created_at).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="py-3 px-4">
                                    {payout.status === "processed" && (
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          onClick={() => handleStripeTransfer(payout.payout_id)}
                                          disabled={transferringPayout === payout.payout_id}
                                          className="bg-[#635BFF] hover:bg-[#5349E0] text-white"
                                        >
                                          {transferringPayout === payout.payout_id ? (
                                            <><Loader2 className="w-3 h-3 animate-spin mr-1" />Transferring</>
                                          ) : (
                                            <><Zap className="w-3 h-3 mr-1" />Transfer via Stripe</>
                                          )}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleMarkTransferred(payout.payout_id)}
                                          className="text-green-600 border-green-200 hover:bg-green-50"
                                        >
                                          <CheckCircle className="w-3 h-3 mr-1" />
                                          Manual
                                        </Button>
                                      </div>
                                    )}
                                    {payout.status === "transferred" && (
                                      <span className="text-xs text-gray-400">
                                        {payout.transferred_at ? new Date(payout.transferred_at).toLocaleDateString() : ''}
                                        {payout.stripe_transfer_id && (
                                          <span className="ml-1 text-[#635BFF]">
                                            (Stripe)
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Auto-Payout Users Tab */}
                <TabsContent value="auto-payout" className="mt-4">
                  <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="font-bold font-['Manrope']">Auto-Payout Users</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">Enterprise and Power users with auto-payout enabled</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={fetchAutoPayoutUsers}
                          disabled={loadingAutoPayoutUsers}
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${loadingAutoPayoutUsers ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-[#121212] hover:bg-[#2a2a2a]"
                          onClick={handleProcessAllAutoPayouts}
                          disabled={processingAllPayouts || autoPayoutUsers.length === 0}
                        >
                          {processingAllPayouts ? (
                            <><Loader2 className="w-4 h-4 animate-spin mr-1" />Processing...</>
                          ) : (
                            <><Zap className="w-4 h-4 mr-1" />Process All Auto-Payouts</>
                          )}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loadingAutoPayoutUsers ? (
                        <div className="text-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Loading auto-payout users...</p>
                        </div>
                      ) : autoPayoutUsers.length === 0 ? (
                        <div className="text-center py-12">
                          <Zap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 font-medium">No auto-payout users</p>
                          <p className="text-sm text-gray-400 mt-1">Enterprise and Power users with auto-payout enabled will appear here</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">User</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Plan</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Stripe Connect</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Pending Amount</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Payment Links</th>
                              </tr>
                            </thead>
                            <tbody>
                              {autoPayoutUsers.map((u) => (
                                <tr key={u.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="py-3 px-4">
                                    <p className="font-medium text-sm">{u.name || 'N/A'}</p>
                                    <p className="text-xs text-gray-500">{u.email}</p>
                                  </td>
                                  <td className="py-3 px-4">
                                    <Badge className={
                                      u.plan === 'power' ? 'bg-purple-100 text-purple-700' :
                                      u.plan === 'enterprise' ? 'bg-[#BFFF00]/30 text-[#121212]' :
                                      'bg-gray-100 text-gray-700'
                                    }>
                                      {u.plan}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-4">
                                    <Badge className={
                                      u.stripe_connect_status === 'connected' ? 'bg-green-100 text-green-700' :
                                      'bg-red-100 text-red-700'
                                    }>
                                      {u.stripe_connect_status === 'connected' ? 'Connected' : 'Not Connected'}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className="font-semibold text-green-600">
                                      ${(u.pending_payout_amount || 0).toLocaleString()}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className="text-sm">{u.pending_payment_links || 0}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Quotes Tab */}
          {activeTab === "quotes" && (
            <div className="space-y-6">
              <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                <CardHeader>
                  <CardTitle className="font-bold font-['Manrope']">Quote Requests ({quotes.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {quotes.length === 0 ? (
                    <p className="text-center text-[#121212]/40 py-8">No quote requests</p>
                  ) : (
                    <div className="space-y-3">
                      {quotes.map(q => (
                        <div key={q.quote_id} className="p-4 bg-[#F7FFE5]/50 rounded-2xl">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-bold text-[#121212]">{q.name}</p>
                              <p className="text-sm text-[#121212]/60">{q.email}</p>
                            </div>
                            <Badge variant="outline" className="rounded-full capitalize">{q.status}</Badge>
                          </div>
                          <p className="text-sm text-[#121212]/70 mb-2">{q.company_name} • {q.entity_type}</p>
                          <div className="flex flex-wrap gap-2">
                            {q.services?.map((s, i) => (
                              <Badge key={i} className="bg-[#BFFF00]/30 text-[#121212] rounded-full">{s}</Badge>
                            ))}
                          </div>
                          {q.message && <p className="text-sm text-[#121212]/60 mt-2 italic">"{q.message}"</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
                <CardHeader>
                  <CardTitle className="font-bold font-['Manrope']">Contact Messages ({contacts.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {contacts.length === 0 ? (
                    <p className="text-center text-[#121212]/40 py-8">No messages</p>
                  ) : (
                    <div className="space-y-3">
                      {contacts.map(c => (
                        <div key={c.contact_id} className="p-4 bg-[#F7FFE5]/50 rounded-2xl">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-bold text-[#121212]">{c.name}</p>
                              <p className="text-sm text-[#121212]/60">{c.email}</p>
                            </div>
                            <Badge variant="outline" className="rounded-full capitalize">{c.status}</Badge>
                          </div>
                          <p className="text-sm text-[#121212]/70">{c.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Role Update Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope']">Update User Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#121212]/60">User</Label>
              <p className="font-medium">{selectedUser?.name}</p>
              <p className="text-sm text-[#121212]/50">{selectedUser?.email}</p>
            </div>
            <div>
              <Label>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleRoleUpdate} className="rounded-full bg-[#121212] hover:bg-[#2a2a2a]">Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Update Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope']">Upgrade User Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-[#F7FFE5] rounded-xl">
              <div className="w-10 h-10 bg-[#121212] rounded-xl flex items-center justify-center text-[#BFFF00] font-bold">
                {selectedUser?.name?.[0] || "U"}
              </div>
              <div>
                <p className="font-medium text-[#121212]">{selectedUser?.name}</p>
                <p className="text-sm text-[#121212]/60">{selectedUser?.email}</p>
                <div className="mt-1">
                  {getPlanBadge(selectedUser?.plan || "free")}
                </div>
              </div>
            </div>
            <div>
              <Label className="font-semibold">Select New Plan</Label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger className="rounded-xl mt-2"><SelectValue placeholder="Choose a plan" /></SelectTrigger>
                <SelectContent>
                  {PLANS.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{p.name}</span>
                        {p.id !== "free" && <Badge className="bg-[#BFFF00]/30 text-[#121212] text-[10px] rounded-full">PAID</Badge>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Plan Features Preview */}
            {newPlan && (
              <div className="p-4 bg-[#F7FFE5]/50 rounded-xl border-2 border-dashed border-[#BFFF00]/30">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-4 h-4 text-[#121212]" />
                  <p className="font-bold text-sm text-[#121212]">{PLANS.find(p => p.id === newPlan)?.name} Features</p>
                </div>
                <ul className="space-y-2">
                  {PLANS.find(p => p.id === newPlan)?.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#121212]/70">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handlePlanUpdate} className="rounded-full bg-[#121212] hover:bg-[#2a2a2a]">
              <Crown className="w-4 h-4 mr-2" />
              Update Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email User Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope']">Send Email to User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-[#F7FFE5] rounded-xl">
              <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-[#121212]" />
              </div>
              <div>
                <p className="font-medium text-[#121212]">{selectedUser?.name}</p>
                <p className="text-sm text-[#121212]/60">{selectedUser?.email}</p>
              </div>
            </div>
            <div>
              <Label>Subject</Label>
              <Input 
                value={emailForm.subject} 
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject..."
                className="rounded-xl mt-2"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea 
                value={emailForm.message} 
                onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Write your message here..."
                rows={5}
                className="rounded-xl mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail} className="rounded-full bg-[#121212] hover:bg-[#2a2a2a]">
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Task Detail Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col rounded-2xl border-2 border-[#BFFF00]/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-bold font-['Manrope']">
              <ClipboardCheck className="w-5 h-5 text-[#BFFF00]" />
              Review Document: {selectedReviewTask?.document_name}
              <Badge className={`ml-2 rounded-full ${selectedReviewTask?.status === "pending" ? 'bg-amber-500 text-white' : 'bg-green-500 text-white'}`}>
                {selectedReviewTask?.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {reviewTaskDetails && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* User Info */}
              <div className="flex items-center justify-between p-3 bg-[#F7FFE5] rounded-lg mb-4 border border-[#BFFF00]/30">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#121212] rounded-xl flex items-center justify-center text-[#BFFF00] font-bold">
                    {selectedReviewTask?.user_name?.[0] || "U"}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{selectedReviewTask?.user_name}</p>
                    <p className="text-xs text-[#121212]/60">{selectedReviewTask?.user_email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveReviewTags}
                    disabled={savingReviewTags}
                    variant="outline"
                    className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
                  >
                    {savingReviewTags ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                  {selectedReviewTask?.status === "pending" && (
                    <Button
                      onClick={() => handleCompleteReview(selectedReviewTask.task_id)}
                      className="rounded-full bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark Complete
                    </Button>
                  )}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Card className="border-2 border-dashed border-green-300 bg-green-50 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Income</span>
                  </div>
                  <p className="text-xl font-bold text-green-700 font-['Manrope']">
                    {formatCurrency(getReviewLiveStats().totalIncome)}
                  </p>
                </Card>
                <Card className="border-2 border-dashed border-red-300 bg-red-50 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-700">Expenses</span>
                  </div>
                  <p className="text-xl font-bold text-red-700 font-['Manrope']">
                    {formatCurrency(getReviewLiveStats().totalExpenses)}
                  </p>
                </Card>
                <Card className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700">Suspense</span>
                  </div>
                  <p className="text-xl font-bold text-amber-700 font-['Manrope']">
                    {getReviewLiveStats().suspenseItems.length}
                  </p>
                </Card>
              </div>

              {/* Transactions Table */}
              <Card className="flex-1 border-2 border-dashed border-[#BFFF00]/30 rounded-xl overflow-hidden">
                <CardHeader className="py-2 px-4 bg-[#F7FFE5]">
                  <CardTitle className="text-sm font-bold">Transactions ({editedReviewTransactions.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-y-auto max-h-[300px]">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7FFE5] sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-[#121212]/70">Date</th>
                        <th className="text-left px-3 py-2 font-semibold text-[#121212]/70">Description</th>
                        <th className="text-left px-3 py-2 font-semibold text-[#121212]/70 w-48">Category</th>
                        <th className="text-right px-3 py-2 font-semibold text-[#121212]/70">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#BFFF00]/10">
                      {editedReviewTransactions.map((txn, i) => (
                        <tr key={i} className={`hover:bg-[#F7FFE5]/50 ${txn.is_suspense || txn.category === "Suspense" ? 'bg-amber-50' : ''}`}>
                          <td className="px-3 py-2 text-[#121212]/70">{txn.date || "-"}</td>
                          <td className="px-3 py-2 text-[#121212] truncate max-w-[200px]" title={txn.description}>
                            {txn.description}
                          </td>
                          <td className="px-3 py-2">
                            <Select
                              value={txn.category || "Suspense"}
                              onValueChange={(val) => handleUpdateReviewCategory(i, val)}
                            >
                              <SelectTrigger className="h-8 text-xs rounded-lg border-[#BFFF00]/30">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Suspense" className="text-amber-600">
                                  <span className="flex items-center gap-1">
                                    <HelpCircle className="w-3 h-3" /> Suspense
                                  </span>
                                </SelectItem>
                                <div className="px-2 py-1 text-xs text-gray-500 font-semibold">Income</div>
                                {INCOME_CATEGORIES.map(cat => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                                <div className="px-2 py-1 text-xs text-gray-500 font-semibold">Expense</div>
                                {EXPENSE_CATEGORIES.map(cat => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className={`px-3 py-2 text-right font-medium ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(txn.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Documents Modal */}
      <Dialog open={showUserDocsModal} onOpenChange={setShowUserDocsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col rounded-2xl border-2 border-[#BFFF00]/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-bold font-['Manrope']">
              <FolderOpen className="w-5 h-5 text-[#BFFF00]" />
              Documents: {selectedDocUser?.name || selectedDocUser?.email}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {/* User Info */}
            <div className="flex items-center gap-3 p-3 bg-[#F7FFE5] rounded-lg mb-4 border border-[#BFFF00]/30">
              <div className="w-10 h-10 bg-[#121212] rounded-xl flex items-center justify-center text-[#BFFF00] font-bold">
                {selectedDocUser?.name?.[0] || "U"}
              </div>
              <div>
                <p className="font-medium">{selectedDocUser?.name}</p>
                <p className="text-sm text-[#121212]/60">{selectedDocUser?.email}</p>
              </div>
              <Badge className="ml-auto rounded-full bg-[#BFFF00]/30 text-[#121212] capitalize">
                {selectedDocUser?.plan || "free"} plan
              </Badge>
            </div>

            {/* Documents List */}
            {userDocuments.length === 0 ? (
              <div className="text-center py-12 text-[#121212]/40">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No documents uploaded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {userDocuments.map(doc => (
                  <div 
                    key={doc.document_id}
                    className="p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#BFFF00] bg-white transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#121212]" />
                        </div>
                        <div>
                          <p className="font-bold text-[#121212]">{doc.filename}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="rounded-full bg-[#BFFF00]/20 text-[#121212] text-xs">
                              {doc.file_type || "PDF"}
                            </Badge>
                            <span className="text-xs text-[#121212]/50">
                              {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "Unknown date"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.comment_count > 0 && (
                          <Badge className="rounded-full bg-blue-100 text-blue-700">
                            {doc.comment_count} comments
                          </Badge>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickLook(doc)}
                          className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
                          data-testid={`quick-look-btn-${doc.document_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> Quick Look
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedDocument(doc); setShowDocDetailModal(true); }}
                          className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
                        >
                          <MessageSquare className="w-4 h-4 mr-1" /> Comment
                        </Button>
                      </div>
                    </div>

                    {/* Show recent comments preview */}
                    {doc.comments && doc.comments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-[#121212]/50 mb-2">Recent comment:</p>
                        <div className="bg-gray-50 rounded-lg p-2 text-sm">
                          <span className="font-medium">{doc.comments[0].commenter_name}:</span>{" "}
                          <span className="text-[#121212]/70">{doc.comments[0].comment.slice(0, 100)}{doc.comments[0].comment.length > 100 ? "..." : ""}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Detail & Comment Modal */}
      <Dialog open={showDocDetailModal} onOpenChange={setShowDocDetailModal}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col rounded-2xl border-2 border-[#BFFF00]/30 p-0">
          <DialogHeader className="p-4 border-b border-[#BFFF00]/20 bg-[#F7FFE5]">
            <DialogTitle className="flex items-center gap-2 font-bold font-['Manrope']">
              <FileText className="w-5 h-5 text-[#BFFF00]" />
              {selectedDocument?.filename || selectedDocument?.original_filename}
            </DialogTitle>
          </DialogHeader>

          {selectedDocument && (
            <div className="flex flex-1 overflow-hidden">
              {/* Left: Document Preview */}
              <div className="flex-1 bg-gray-100 overflow-hidden flex flex-col">
                {/* Preview Area */}
                <div className="flex-1 overflow-auto p-4">
                  {(() => {
                    const fileUrl = getFileUrl(selectedDocument);
                    const previewType = getPreviewType(selectedDocument.filename || selectedDocument.original_filename, selectedDocument.file_type);
                    
                    if (!fileUrl) {
                      return (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center p-8">
                            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                            <h4 className="font-bold text-lg text-[#121212] mb-2">Preview not available</h4>
                            <p className="text-[#121212]/60 text-sm">Document URL is not available.</p>
                          </div>
                        </div>
                      );
                    }
                    
                    if (previewType === 'pdf') {
                      return (
                        <object
                          data={fileUrl}
                          type="application/pdf"
                          className="w-full h-full min-h-[400px] rounded-lg"
                        >
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                              <FileText className="w-12 h-12 text-[#121212]/40 mx-auto mb-3" />
                              <h4 className="font-bold text-lg text-[#121212] mb-2">PDF Preview</h4>
                              <p className="text-[#121212]/60 text-sm mb-4">Your browser cannot display this PDF inline.</p>
                              <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#121212] text-white rounded-full font-medium hover:bg-[#2a2a2a]">
                                <Eye className="w-4 h-4" /> Open PDF
                              </a>
                            </div>
                          </div>
                        </object>
                      );
                    }
                    
                    if (previewType === 'image') {
                      return (
                        <div className="h-full flex items-center justify-center">
                          <img
                            src={fileUrl}
                            alt={selectedDocument.filename}
                            className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                          />
                        </div>
                      );
                    }
                    
                    // For spreadsheets (xls, xlsx, csv) - can't preview inline
                    if (previewType === 'spreadsheet') {
                      return (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                            <FileText className="w-12 h-12 text-green-600 mx-auto mb-3" />
                            <h4 className="font-bold text-lg text-[#121212] mb-2">Spreadsheet File</h4>
                            <p className="text-[#121212]/60 text-sm mb-4">
                              {selectedDocument.file_type?.toUpperCase() || 'XLS'} files cannot be previewed in browser.
                            </p>
                            <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-6 py-3 bg-[#121212] text-white rounded-full font-medium hover:bg-[#2a2a2a]">
                              <Download className="w-4 h-4" /> Download to View
                            </a>
                          </div>
                        </div>
                      );
                    }
                    
                    // Unsupported
                    return (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                          <FileText className="w-12 h-12 text-[#121212]/40 mx-auto mb-3" />
                          <h4 className="font-bold text-lg text-[#121212] mb-2">Preview not available</h4>
                          <p className="text-[#121212]/60 text-sm mb-4">This file type cannot be previewed.</p>
                          <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[#121212] text-white rounded-full font-medium hover:bg-[#2a2a2a]">
                            <Download className="w-4 h-4" /> Download File
                          </a>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Download Bar */}
                <div className="p-3 bg-white border-t border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className="rounded-full bg-[#BFFF00]/20 text-[#121212]">
                      {selectedDocument.file_type?.toUpperCase() || "PDF"}
                    </Badge>
                    <span className="text-sm text-[#121212]/60">
                      {selectedDocument.file_size ? `${(selectedDocument.file_size / 1024).toFixed(1)} KB` : ""}
                    </span>
                  </div>
                  {selectedDocument.file_url && (
                    <a 
                      href={getFileUrl(selectedDocument)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#121212] text-white rounded-full text-sm font-medium hover:bg-[#2a2a2a]"
                    >
                      <Download className="w-4 h-4" /> Download
                    </a>
                  )}
                </div>
              </div>
              
              {/* Right: Comments Panel */}
              <div className="w-80 border-l border-[#BFFF00]/20 flex flex-col bg-white">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-bold text-[#121212] flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Comments ({selectedDocument.comments?.length || 0})
                  </h3>
                </div>
                
                {/* Comments List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {selectedDocument.comments?.length === 0 ? (
                    <p className="text-sm text-[#121212]/50 text-center py-4">No comments yet</p>
                  ) : (
                    selectedDocument.comments?.map((comment, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${comment.is_admin_comment ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{comment.commenter_name}</span>
                          <span className="text-xs text-[#121212]/50">
                            {comment.created_at ? new Date(comment.created_at).toLocaleString() : ""}
                          </span>
                        </div>
                        <p className="text-sm text-[#121212]/80">{comment.comment}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Comment */}
                <div className="p-4 border-t border-gray-100">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write your comment..."
                    className="mb-3 border-[#BFFF00]/30 focus:ring-2 focus:ring-[#BFFF00]/50 rounded-xl text-sm"
                    rows={3}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="notify-detail"
                        checked={notifyOnComment}
                        onCheckedChange={(checked) => setNotifyOnComment(checked)}
                        className="w-4 h-4"
                      />
                      <label htmlFor="notify-detail" className="text-xs text-[#121212]/70 cursor-pointer flex items-center gap-1">
                        <Bell className="w-3 h-3" /> Notify
                      </label>
                    </div>
                    <Button
                      onClick={handleAddComment}
                      disabled={sendingComment || !newComment.trim()}
                      size="sm"
                      className="rounded-full bg-[#121212] hover:bg-[#2a2a2a]"
                    >
                      {sendingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Look Document Preview Modal */}
      <Dialog open={showQuickLook} onOpenChange={setShowQuickLook}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col rounded-2xl border-2 border-[#BFFF00]/30 p-0" data-testid="quick-look-modal">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#BFFF00]/20 bg-[#F7FFE5]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#121212] rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#BFFF00]" />
              </div>
              <div>
                <h3 className="font-bold text-[#121212] font-['Manrope']">{quickLookDoc?.filename}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge className="rounded-full bg-[#BFFF00]/20 text-[#121212] text-xs">
                    {quickLookDoc?.file_type || "PDF"}
                  </Badge>
                  <span className="text-xs text-[#121212]/50">
                    {quickLookDoc?.file_size ? `${(quickLookDoc.file_size / 1024).toFixed(1)} KB` : ""}
                  </span>
                  <span className="text-xs text-[#121212]/50">
                    {quickLookDoc?.created_at ? new Date(quickLookDoc.created_at).toLocaleDateString() : ""}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {quickLookDoc?.file_url && (
                <a 
                  href={getFileUrl(quickLookDoc)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#121212] text-white rounded-full text-sm font-medium hover:bg-[#2a2a2a] transition-colors"
                >
                  <Download className="w-4 h-4" /> Download
                </a>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => { 
                  setSelectedDocument(quickLookDoc); 
                  setShowQuickLook(false);
                  setShowDocDetailModal(true); 
                }}
                className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
              >
                <MessageSquare className="w-4 h-4 mr-1" /> Comments
              </Button>
            </div>
          </div>

          {/* Preview Area */}
          <div className="flex-1 bg-gray-100 overflow-auto relative" style={{ minHeight: '500px', maxHeight: '70vh' }}>
            {quickLookDoc && (
              <>
                {/* Loading State */}
                {previewLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                    <div className="text-center">
                      <Loader2 className="w-10 h-10 animate-spin text-[#BFFF00] mx-auto mb-3" />
                      <p className="text-sm text-[#121212]/60">Loading preview...</p>
                    </div>
                  </div>
                )}

                {/* PDF Preview using object tag with fallback */}
                {getPreviewType(quickLookDoc.filename, quickLookDoc.file_type) === 'pdf' && quickLookDoc.file_url && (
                  <object
                    data={getFileUrl(quickLookDoc)}
                    type="application/pdf"
                    className="w-full h-full"
                    style={{ minHeight: '600px' }}
                    onLoad={() => setPreviewLoading(false)}
                  >
                    {/* Fallback for browsers that can't display PDF inline */}
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md mx-4">
                        <div className="w-16 h-16 bg-[#F7FFE5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-[#121212]" />
                        </div>
                        <h4 className="font-bold text-lg text-[#121212] mb-2">PDF Preview</h4>
                        <p className="text-[#121212]/60 text-sm mb-4">
                          Your browser cannot display this PDF inline. Click below to view it.
                        </p>
                        <a 
                          href={getFileUrl(quickLookDoc)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#121212] text-white rounded-full font-medium hover:bg-[#2a2a2a] transition-colors"
                        >
                          <Eye className="w-4 h-4" /> Open PDF
                        </a>
                      </div>
                    </div>
                  </object>
                )}

                {/* Image Preview */}
                {getPreviewType(quickLookDoc.filename, quickLookDoc.file_type) === 'image' && quickLookDoc.file_url && (
                  <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                    <img
                      src={getFileUrl(quickLookDoc)}
                      alt={quickLookDoc.filename}
                      className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                      onLoad={() => setPreviewLoading(false)}
                      onError={() => setPreviewLoading(false)}
                    />
                  </div>
                )}

                {/* Unsupported File Type */}
                {(getPreviewType(quickLookDoc.filename, quickLookDoc.file_type) === 'unsupported' || 
                  getPreviewType(quickLookDoc.filename, quickLookDoc.file_type) === 'doc' ||
                  getPreviewType(quickLookDoc.filename, quickLookDoc.file_type) === 'spreadsheet') && (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md mx-4">
                      <div className="w-16 h-16 bg-[#F7FFE5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-[#121212]" />
                      </div>
                      <h4 className="font-bold text-lg text-[#121212] mb-2">Preview not available</h4>
                      <p className="text-[#121212]/60 text-sm mb-4">
                        This file type ({quickLookDoc.file_type || quickLookDoc.filename?.split('.').pop()?.toUpperCase()}) cannot be previewed directly in the browser.
                      </p>
                      {quickLookDoc.file_url && (
                        <a 
                          href={getFileUrl(quickLookDoc)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#121212] text-white rounded-full font-medium hover:bg-[#2a2a2a] transition-colors"
                        >
                          <Download className="w-4 h-4" /> Download to View
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* No URL available */}
                {!quickLookDoc.file_url && (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center p-8">
                      <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                      <h4 className="font-bold text-lg text-[#121212] mb-2">File URL not available</h4>
                      <p className="text-[#121212]/60 text-sm">The document URL is missing or expired.</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* AI Analysis Preview (if available) */}
          {quickLookDoc?.ai_analysis && (
            <div className="p-4 border-t border-[#BFFF00]/20 bg-[#F7FFE5]/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-[#BFFF00] rounded-lg flex items-center justify-center">
                  <span className="text-[10px] font-bold text-[#121212]">AI</span>
                </div>
                <span className="font-medium text-sm text-[#121212]">AI Analysis Summary</span>
              </div>
              <p className="text-sm text-[#121212]/70 line-clamp-2">
                {typeof quickLookDoc.ai_analysis === 'string' 
                  ? quickLookDoc.ai_analysis 
                  : quickLookDoc.ai_analysis?.summary || 'Analysis available'}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document/Task Request Dialog */}
      <Dialog open={showDocRequestModal} onOpenChange={setShowDocRequestModal}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope'] flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#BFFF00]" />
              Create Task for User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Task Type Selection */}
            <div>
              <Label className="font-semibold">Task Type *</Label>
              <Select 
                value={docRequestForm.task_type} 
                onValueChange={(value) => setDocRequestForm(prev => ({ ...prev, task_type: value }))}
              >
                <SelectTrigger className="rounded-xl mt-2">
                  <SelectValue placeholder="Select task type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="document_upload">
                    <div className="flex items-center gap-2">
                      <Upload className="w-4 h-4 text-blue-600" />
                      Document Upload
                    </div>
                  </SelectItem>
                  <SelectItem value="form_completion">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      Form Completion
                    </div>
                  </SelectItem>
                  <SelectItem value="review_acknowledge">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Review & Acknowledge
                    </div>
                  </SelectItem>
                  <SelectItem value="information_request">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-amber-600" />
                      Information Request
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="w-4 h-4 text-gray-600" />
                      Custom Task
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* User Selection */}
            <div>
              <Label className="font-semibold">Select User *</Label>
              <Select 
                value={docRequestForm.user_id} 
                onValueChange={(value) => {
                  const selectedUserData = users.find(u => u.user_id === value);
                  setDocRequestForm(prev => ({
                    ...prev,
                    user_id: value,
                    user_email: selectedUserData?.email || ""
                  }));
                }}
              >
                <SelectTrigger className="rounded-xl mt-2">
                  <SelectValue placeholder="Choose a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.role === "user").map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{u.name || u.email}</span>
                        <span className="text-xs text-gray-500">{u.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div>
              <Label className="font-semibold">Priority</Label>
              <Select 
                value={docRequestForm.priority} 
                onValueChange={(value) => setDocRequestForm(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger className="rounded-xl mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low"><Badge className="bg-green-100 text-green-700 rounded-full">Low</Badge></SelectItem>
                  <SelectItem value="medium"><Badge className="bg-yellow-100 text-yellow-700 rounded-full">Medium</Badge></SelectItem>
                  <SelectItem value="high"><Badge className="bg-orange-100 text-orange-700 rounded-full">High</Badge></SelectItem>
                  <SelectItem value="urgent"><Badge className="bg-red-100 text-red-700 rounded-full">Urgent</Badge></SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Document Category - Only show for document_upload */}
            {docRequestForm.task_type === "document_upload" && (
              <div>
                <Label className="font-semibold">Document Category</Label>
                <Select 
                  value={docRequestForm.category} 
                  onValueChange={(value) => setDocRequestForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger className="rounded-xl mt-2">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoices">Invoices</SelectItem>
                    <SelectItem value="company_documents">Company Documents</SelectItem>
                    <SelectItem value="compliance_documents">Compliance Documents</SelectItem>
                    <SelectItem value="past_returns">Past Returns</SelectItem>
                    <SelectItem value="bank_statements">Bank Statements</SelectItem>
                    <SelectItem value="credit_card_statements">Credit Card Statements</SelectItem>
                    <SelectItem value="expense_sheets">Expense Sheets</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Title */}
            <div>
              <Label className="font-semibold">Title *</Label>
              <Input 
                value={docRequestForm.title}
                onChange={(e) => setDocRequestForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder={docRequestForm.task_type === "document_upload" ? "e.g., Q4 2025 Bank Statement" : "Task title"}
                className="rounded-xl mt-2"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="font-semibold">Description</Label>
              <Textarea 
                value={docRequestForm.description}
                onChange={(e) => setDocRequestForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Additional details..."
                className="rounded-xl mt-2"
                rows={3}
              />
            </div>

            {/* Due Date */}
            <div>
              <Label className="font-semibold">Due Date (optional)</Label>
              <Input 
                type="date"
                value={docRequestForm.due_date}
                onChange={(e) => setDocRequestForm(prev => ({ ...prev, due_date: e.target.value }))}
                className="rounded-xl mt-2"
              />
            </div>

            <div className="p-3 bg-[#F7FFE5] rounded-xl text-sm text-[#121212]/70">
              <p>The user will receive:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>An email notification with your request</li>
                <li>A task in their dashboard to upload the document</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocRequestModal(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleDocumentRequest}
              disabled={sendingDocRequest || !docRequestForm.user_id || !docRequestForm.title}
              className="rounded-full bg-[#121212] hover:bg-[#2a2a2a] gap-2"
            >
              {sendingDocRequest ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DocuHub User Modal */}
      <Dialog open={showDocuhubModal} onOpenChange={setShowDocuhubModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col rounded-2xl border-2 border-[#BFFF00]/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-bold font-['Manrope']">
              <FolderArchive className="w-5 h-5 text-[#BFFF00]" />
              DocuHub: {selectedDocuhubUser?.name || selectedDocuhubUser?.email}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {docuhubLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#BFFF00]" />
              </div>
            ) : docuhubUserData ? (
              <div className="space-y-4">
                {/* User Info */}
                <div className="flex items-center gap-3 p-3 bg-[#F7FFE5] rounded-lg border border-[#BFFF00]/30">
                  <div className="w-10 h-10 bg-[#121212] rounded-xl flex items-center justify-center text-[#BFFF00] font-bold">
                    {docuhubUserData.user?.name?.[0] || "U"}
                  </div>
                  <div>
                    <p className="font-medium">{docuhubUserData.user?.name}</p>
                    <p className="text-sm text-[#121212]/60">{docuhubUserData.user?.email}</p>
                  </div>
                  {docuhubUserData.user?.company_name && (
                    <Badge className="ml-auto rounded-full bg-[#BFFF00]/30 text-[#121212]">
                      {docuhubUserData.user?.company_name}
                    </Badge>
                  )}
                </div>

                {/* Folders */}
                {docuhubUserData.folders?.map(folder => (
                  <div key={folder.folder} className="border-2 border-dashed border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
                      <FolderOpen className="w-4 h-4 text-[#121212]" />
                      <span className="font-medium capitalize">{folder.folder?.replace(/_/g, ' ')}</span>
                      <Badge className="ml-auto bg-gray-100 text-gray-600 rounded-full">
                        {folder.documents?.length || 0} files
                      </Badge>
                    </div>
                    {folder.documents?.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {folder.documents.map(doc => (
                          <div 
                            key={doc.document_id} 
                            className={`p-4 hover:bg-[#F7FFE5]/50 transition-colors ${doc.reviewed ? 'bg-green-50/30' : ''}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${doc.reviewed ? 'bg-green-100' : 'bg-[#F7FFE5]'}`}>
                                  <FileText className={`w-5 h-5 ${doc.reviewed ? 'text-green-600' : 'text-[#121212]'}`} />
                                </div>
                                <div>
                                  <p className="font-medium text-[#121212]">{doc.original_filename}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-[#121212]/50">
                                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 
                                       doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}
                                    </span>
                                    {doc.reviewed && (
                                      <Badge className="bg-green-100 text-green-700 text-xs rounded-full">
                                        <Check className="w-3 h-3 mr-1" />
                                        Reviewed by {doc.reviewed_by_name}
                                      </Badge>
                                    )}
                                    {doc.comment_count > 0 && (
                                      <Badge className="bg-blue-100 text-blue-700 text-xs rounded-full">
                                        {doc.comment_count} comments
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedDocuhubDoc(doc);
                                    fetchDocuhubComments(doc.document_id);
                                    setShowDocuhubPreview(true);
                                  }}
                                  className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
                                >
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </Button>
                                {!isViewOnly && (
                                  <Button
                                    variant={doc.reviewed ? "outline" : "default"}
                                    size="sm"
                                    onClick={() => handleMarkReviewed(doc.document_id, doc.reviewed)}
                                    disabled={markingReviewed}
                                    className={`rounded-full ${doc.reviewed ? 'border-green-500 text-green-700 hover:bg-green-50' : 'bg-green-600 hover:bg-green-700'}`}
                                  >
                                    {markingReviewed ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : doc.reviewed ? (
                                      <>Unmark</>
                                    ) : (
                                      <><Check className="w-4 h-4 mr-1" /> Mark Reviewed</>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="p-4 text-center text-gray-400 text-sm">No documents in this folder</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-[#121212]/40">
                <FolderArchive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No data available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* DocuHub Document Preview Modal */}
      <Dialog open={showDocuhubPreview} onOpenChange={setShowDocuhubPreview}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col rounded-2xl border-2 border-[#BFFF00]/30 p-0">
          <DialogHeader className="p-4 border-b border-[#BFFF00]/20 bg-[#F7FFE5]">
            <DialogTitle className="flex items-center gap-2 font-bold font-['Manrope']">
              <FileText className="w-5 h-5 text-[#BFFF00]" />
              {selectedDocuhubDoc?.original_filename}
              {selectedDocuhubDoc?.reviewed && (
                <Badge className="bg-green-100 text-green-700 rounded-full ml-2">
                  <Check className="w-3 h-3 mr-1" /> Reviewed
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 flex overflow-hidden">
            {/* Document Preview */}
            <div className="flex-1 bg-gray-100 flex items-center justify-center overflow-auto">
              {selectedDocuhubDoc && (
                (() => {
                  const fileType = selectedDocuhubDoc.file_type?.toLowerCase();
                  const url = getDocuhubFileUrl(selectedDocuhubDoc);
                  
                  if (['pdf'].includes(fileType)) {
                    return (
                      <object
                        data={url}
                        type="application/pdf"
                        className="w-full h-full"
                      >
                        <div className="flex flex-col items-center justify-center h-full">
                          <p className="text-gray-500 mb-4">Unable to display PDF inline</p>
                          <Button onClick={() => window.open(url, '_blank')}>
                            <Download className="w-4 h-4 mr-2" /> Open PDF
                          </Button>
                        </div>
                      </object>
                    );
                  } else if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileType)) {
                    return (
                      <img 
                        src={url} 
                        alt={selectedDocuhubDoc.original_filename}
                        className="max-w-full max-h-full object-contain"
                      />
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center">
                        <FileText className="w-16 h-16 text-gray-400 mb-4" />
                        <p className="text-gray-500 mb-4">Preview not available</p>
                        <Button onClick={() => window.open(url, '_blank')}>
                          <Download className="w-4 h-4 mr-2" /> Download
                        </Button>
                      </div>
                    );
                  }
                })()
              )}
            </div>

            {/* Comments Panel */}
            <div className="w-80 border-l border-[#BFFF00]/20 flex flex-col bg-white">
              <div className="p-4 border-b border-[#BFFF00]/20 bg-[#F7FFE5]/50">
                <h3 className="font-bold text-[#121212] flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Comments ({docuhubComments.length})
                </h3>
              </div>

              {/* Comments List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {docuhubComments.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-4">No comments yet</p>
                ) : (
                  docuhubComments.map((comment, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-[#121212]">{comment.commenter_name}</span>
                        <span className="text-xs text-gray-400">
                          {comment.created_at ? new Date(comment.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <p className="text-sm text-[#121212]/70">{comment.comment}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Add Comment */}
              {!isViewOnly && (
                <div className="p-4 border-t border-[#BFFF00]/20">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a comment..."
                      value={docuhubComment}
                      onChange={(e) => setDocuhubComment(e.target.value)}
                      className="flex-1 rounded-full border-[#BFFF00]/30"
                      data-testid="docuhub-comment-input"
                    />
                    <Button
                      onClick={handleDocuhubComment}
                      disabled={sendingDocuhubComment || !docuhubComment.trim()}
                      className="rounded-full bg-[#121212] hover:bg-[#2a2a2a]"
                      data-testid="docuhub-send-comment-btn"
                    >
                      {sendingDocuhubComment ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Mark as Reviewed Button */}
              {!isViewOnly && selectedDocuhubDoc && (
                <div className="p-4 border-t border-[#BFFF00]/20 bg-[#F7FFE5]/30">
                  <Button
                    variant={selectedDocuhubDoc.reviewed ? "outline" : "default"}
                    className={`w-full rounded-full ${
                      selectedDocuhubDoc.reviewed 
                        ? 'border-green-500 text-green-700 hover:bg-green-50' 
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    onClick={() => handleMarkReviewed(selectedDocuhubDoc.document_id, selectedDocuhubDoc.reviewed)}
                    disabled={markingReviewed}
                  >
                    {markingReviewed ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : selectedDocuhubDoc.reviewed ? (
                      <>Unmark as Reviewed</>
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> Mark as Reviewed</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Link Creation Modal */}
      <Dialog open={showPaymentLinkModal} onOpenChange={setShowPaymentLinkModal}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope'] flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-[#BFFF00] flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-[#121212]" />
              </div>
              Create Payment Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Recipient Selection */}
            {recipients.length > 0 && (
              <div>
                <Label className="text-[#121212]/60 text-sm">Select Existing Recipient (Optional)</Label>
                <select
                  className="w-full mt-1.5 px-4 py-3 border border-[#121212]/20 rounded-xl bg-white text-sm"
                  onChange={(e) => {
                    const recipient = recipients.find(r => r.recipient_id === e.target.value);
                    if (recipient) handleSelectRecipient(recipient);
                  }}
                  defaultValue=""
                >
                  <option value="">-- Or enter new client below --</option>
                  {recipients.map(r => (
                    <option key={r.recipient_id} value={r.recipient_id}>
                      {r.name} ({r.email}) - {r.type}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#121212]/60 text-sm">Client Name *</Label>
                <Input
                  value={paymentLinkForm.client_name}
                  onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="John Smith"
                  className="mt-1.5 rounded-xl border-[#121212]/20"
                  data-testid="payment-link-client-name"
                />
              </div>
              <div>
                <Label className="text-[#121212]/60 text-sm">Client Email *</Label>
                <Input
                  type="email"
                  value={paymentLinkForm.client_email}
                  onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, client_email: e.target.value }))}
                  placeholder="john@company.com"
                  className="mt-1.5 rounded-xl border-[#121212]/20"
                  data-testid="payment-link-client-email"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#121212]/60 text-sm">Description *</Label>
              <Input
                value={paymentLinkForm.description}
                onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Consulting services - January 2026"
                className="mt-1.5 rounded-xl border-[#121212]/20"
                data-testid="payment-link-description"
              />
            </div>

            <div>
              <Label className="text-[#121212]/60 text-sm">Amount (USD) *</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#121212]/60">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentLinkForm.amount}
                  onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="pl-8 rounded-xl border-[#121212]/20"
                  data-testid="payment-link-amount"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#121212]/60 text-sm">Notes / Remarks (Optional)</Label>
              <textarea
                value={paymentLinkForm.notes}
                onChange={(e) => setPaymentLinkForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional details about this payment..."
                rows={3}
                className="w-full mt-1.5 px-4 py-3 border border-[#121212]/20 rounded-xl text-sm resize-none"
                data-testid="payment-link-notes"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => handleCreatePaymentLink(false)}
              disabled={creatingPaymentLink}
              className="flex-1 bg-[#121212] hover:bg-[#2a2a2a] text-white rounded-full"
              data-testid="create-and-copy-btn"
            >
              {creatingPaymentLink ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              Create & Copy Link
            </Button>
            <Button
              onClick={() => handleCreatePaymentLink(true)}
              disabled={creatingPaymentLink}
              variant="outline"
              className="flex-1 rounded-full border-[#BFFF00] hover:bg-[#BFFF00]/20"
              data-testid="create-and-send-btn"
            >
              {creatingPaymentLink ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Create & Send Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Edit Modal */}
      <Dialog open={userEditModalOpen} onOpenChange={setUserEditModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] font-bold">Edit User</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-[#F7FFE5] rounded-xl">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt={selectedUser.name} className="w-12 h-12 rounded-xl" />
                ) : (
                  <div className="w-12 h-12 bg-[#BFFF00]/30 rounded-xl flex items-center justify-center">
                    <User className="w-6 h-6 text-[#121212]" />
                  </div>
                )}
                <div>
                  <p className="font-semibold">{selectedUser.name}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <Input
                    value={userEditForm.name}
                    onChange={(e) => setUserEditForm({...userEditForm, name: e.target.value})}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <Input
                    type="email"
                    value={userEditForm.email}
                    onChange={(e) => setUserEditForm({...userEditForm, email: e.target.value})}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={userEditForm.role}
                    onChange={(e) => setUserEditForm({...userEditForm, role: e.target.value})}
                    className="mt-1 w-full rounded-xl border border-gray-200 p-2 focus:ring-2 focus:ring-[#BFFF00]"
                  >
                    <option value="user">User</option>
                    <option value="finance">Finance</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Plan</label>
                  <select
                    value={userEditForm.plan}
                    onChange={(e) => setUserEditForm({...userEditForm, plan: e.target.value})}
                    className="mt-1 w-full rounded-xl border border-gray-200 p-2 focus:ring-2 focus:ring-[#BFFF00]"
                  >
                    <option value="free">Free / Starter</option>
                    <option value="growth">Growth</option>
                    <option value="scale">Scale</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="power">Power User</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Company Name</label>
                  <Input
                    value={userEditForm.company_name}
                    onChange={(e) => setUserEditForm({...userEditForm, company_name: e.target.value})}
                    className="mt-1 rounded-xl"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <Input
                    value={userEditForm.phone}
                    onChange={(e) => setUserEditForm({...userEditForm, phone: e.target.value})}
                    className="mt-1 rounded-xl"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Reset Password</label>
                <Input
                  type="password"
                  value={userEditForm.new_password}
                  onChange={(e) => setUserEditForm({...userEditForm, new_password: e.target.value})}
                  className="mt-1 rounded-xl"
                  placeholder="Leave blank to keep current password"
                />
                <p className="text-xs text-gray-400 mt-1">Enter a new password to reset it (min 6 characters)</p>
              </div>

              {(userEditForm.plan === "enterprise" || userEditForm.plan === "power") && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <div>
                    <p className="font-medium text-amber-800">Auto-Payout</p>
                    <p className="text-xs text-amber-600">Enable automatic daily payouts for this user</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userEditForm.auto_payout_enabled}
                      onChange={(e) => setUserEditForm({...userEditForm, auto_payout_enabled: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#BFFF00]"></div>
                  </label>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setUserEditModalOpen(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveUserEdit}
                  disabled={savingUser}
                  className="flex-1 rounded-full bg-[#121212] hover:bg-[#2a2a2a]"
                >
                  {savingUser ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save Changes</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Limits Management Modal */}
      <Dialog open={limitsModalOpen} onOpenChange={setLimitsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] font-bold">Manage User Limits</DialogTitle>
          </DialogHeader>
          {selectedUserForLimits && userLimitsData && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-[#F7FFE5] rounded-xl">
                <div className="w-10 h-10 bg-[#BFFF00]/30 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-[#121212]" />
                </div>
                <div>
                  <p className="font-semibold">{selectedUserForLimits.name}</p>
                  <p className="text-sm text-gray-500">{selectedUserForLimits.email}</p>
                  <Badge className={`mt-1 ${selectedUserForLimits.plan === 'enterprise' ? 'bg-[#BFFF00]/30' : selectedUserForLimits.plan === 'power' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}>
                    {PLAN_FEATURES[selectedUserForLimits.plan || 'free']?.name || selectedUserForLimits.plan}
                  </Badge>
                </div>
              </div>

              {/* Current Usage */}
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Current Usage</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-lg font-bold">{userLimitsData.current_usage?.invoices || 0}</p>
                    <p className="text-xs text-gray-500">Invoices</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{userLimitsData.current_usage?.payment_links || 0}</p>
                    <p className="text-xs text-gray-500">Payment Links</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{userLimitsData.current_usage?.customers || 0}</p>
                    <p className="text-xs text-gray-500">Customers</p>
                  </div>
                </div>
              </div>

              {/* Limits Form */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase">Set Custom Limits (-1 = Unlimited)</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Invoices</label>
                    <Input
                      type="number"
                      value={limitsForm.invoices}
                      onChange={(e) => setLimitsForm({...limitsForm, invoices: parseInt(e.target.value) || 0})}
                      className="mt-1 rounded-xl"
                      placeholder={`Plan default: ${userLimitsData.plan_defaults?.invoices || 10}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Payment Links</label>
                    <Input
                      type="number"
                      value={limitsForm.payment_links}
                      onChange={(e) => setLimitsForm({...limitsForm, payment_links: parseInt(e.target.value) || 0})}
                      className="mt-1 rounded-xl"
                      placeholder={`Plan default: ${userLimitsData.plan_defaults?.payment_links || 10}`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Customers</label>
                    <Input
                      type="number"
                      value={limitsForm.customers}
                      onChange={(e) => setLimitsForm({...limitsForm, customers: parseInt(e.target.value) || 0})}
                      className="mt-1 rounded-xl"
                      placeholder={`Plan default: ${userLimitsData.plan_defaults?.customers || 10}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Vendors</label>
                    <Input
                      type="number"
                      value={limitsForm.vendors}
                      onChange={(e) => setLimitsForm({...limitsForm, vendors: parseInt(e.target.value) || 0})}
                      className="mt-1 rounded-xl"
                      placeholder={`Plan default: ${userLimitsData.plan_defaults?.vendors || 10}`}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Custom Platform Fee (%)</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={limitsForm.custom_platform_fee}
                    onChange={(e) => setLimitsForm({...limitsForm, custom_platform_fee: parseFloat(e.target.value) || 0})}
                    className="mt-1 rounded-xl"
                    placeholder={`Plan default: ${userLimitsData.plan_default_fee || 1}%`}
                  />
                  <p className="text-xs text-gray-400 mt-1">Set 0 for no fee (VIP), or custom rate. Plan default: {userLimitsData.plan_default_fee || 1}%</p>
                </div>
              </div>

              {userLimitsData.has_custom_overrides && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-700">This user has custom overrides applied</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleResetLimitsToDefaults}
                  disabled={savingLimits}
                  className="rounded-full"
                >
                  Reset to Defaults
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setLimitsModalOpen(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveLimits}
                  disabled={savingLimits}
                  className="flex-1 rounded-full bg-[#121212] hover:bg-[#2a2a2a]"
                >
                  {savingLimits ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save Limits</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign User to Bookkeeper Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="rounded-2xl border-2 border-blue-300 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope'] flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center">
                <Plus className="w-4 h-4 text-blue-600" />
              </div>
              Assign User to {selectedBookkeeper?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-600 text-sm">Select User to Assign</Label>
              <select
                className="w-full mt-1.5 px-4 py-3 border border-gray-200 rounded-xl bg-white text-sm"
                value={selectedUserToAssign}
                onChange={(e) => setSelectedUserToAssign(e.target.value)}
                data-testid="assign-user-select"
              >
                <option value="">-- Select a user --</option>
                {users
                  .filter(u => u.role === "user" && !bookkeeperAssignments.find(a => a.user_id === u.user_id))
                  .map(u => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email}) - {u.plan || 'Free'}
                    </option>
                  ))
                }
              </select>
            </div>
            <p className="text-xs text-gray-500">
              Only users with "User" role who are not already assigned to this bookkeeper are shown.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAssignModal(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignUser}
              disabled={!selectedUserToAssign || assigningUser}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
              data-testid="confirm-assign-btn"
            >
              {assigningUser ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Assigning...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" />Assign User</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
